import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  ArchitectureVisual,
  CodeVisual,
  ComingSoonVisual,
  McpUrlVisual,
  MemoryRecallVisual,
  OAuthFlowVisual,
  PermissionMatrixVisual,
  ProjectHierarchyVisual,
  QuickstartStep1Visual,
  QuickstartStep2Visual,
  QuickstartStep3Visual,
  QuickstartStep4Visual,
  QuickstartStep5Visual,
  RestRequestVisual,
  ToolExportVisual,
  VectorRecallVisual,
} from "@/app/docs/visuals";

interface DocPageEntry {
  section: string;
  title: string;
  description: string;
  /** Animated illustration rendered above the prose body. */
  visual?: ReactNode;
  body: ReactNode;
}

const Soon = () => (
  <Badge variant="outline" className="ml-2 align-middle">
    coming soon
  </Badge>
);

const ProseList = ({ items }: { items: ReactNode[] }) => (
  <ul>
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const CodeBlock = ({ lang, children }: { lang?: string; children: string }) => (
  <pre data-lang={lang}>
    <code>{children}</code>
  </pre>
);

// ── Page bodies ──────────────────────────────────────────────────────

const IntroBody = (
  <>
    <p>
      MemKernel is the control plane your AI agents talk through. It manages tools, MCP servers,
      memory, vectors, identity, and permissions in one place — and exposes the result through MCP,
      OpenAI Function calling, Anthropic Tools, LangChain, Mastra, and plain REST. Connect once on
      MemKernel; ship the agent anywhere.
    </p>
    <p>
      The pitch in one sentence:{" "}
      <em>
        define a Gmail tool once, and Claude Desktop, Cursor, ChatGPT, and your own TypeScript agent
        all reach it without you copying a schema between codebases.
      </em>
    </p>
    <h2>What this means in practice</h2>
    <ProseList
      items={[
        <>
          You hit <strong>Connect Google</strong> once on memkernel.com — OAuth, scopes, token
          refresh, encryption are handled for you.
        </>,
        <>
          Memkernel exposes a per-project MCP URL like <code>mcp.memkernel.com/p/your-project</code>
          . Paste it into Claude Desktop or Cursor and the project's whole loadout is immediately
          callable.
        </>,
        <>
          From code, the SDK returns the same tools in whatever shape your model expects — Anthropic
          schema, OpenAI function calling, LangChain tools, Mastra tools, or raw JSON Schema.
        </>,
        <>
          Permissions decide which client gets which tool: Claude can read Gmail; Cursor can push to
          GitHub; the research agent sees Notion only.
        </>,
      ]}
    />

    <h2>Where to start</h2>
    <p>Pick the path that matches what you're trying to do.</p>
    <ProseList
      items={[
        <>
          <strong>Just trying it out:</strong> <Link to="/docs/getting-started">Quickstart</Link> —
          connect Google Drive and expose it to Claude Desktop in five minutes.
        </>,
        <>
          <strong>Building an agent in code:</strong>{" "}
          <Link to="/docs/sdks/typescript">TypeScript SDK</Link>,{" "}
          <Link to="/docs/sdks/python">Python SDK</Link>, or <Link to="/docs/sdks/go">Go SDK</Link>.
        </>,
        <>
          <strong>Exposing a project to MCP clients:</strong>{" "}
          <Link to="/docs/api/mcp">MCP API reference</Link>.
        </>,
        <>
          <strong>Hitting the HTTP surface directly:</strong>{" "}
          <Link to="/docs/api/rest">REST API reference</Link>.
        </>,
        <>
          <strong>Understanding the building blocks:</strong> read the{" "}
          <Link to="/docs/concepts/projects">Core concepts</Link> series — projects, tools, MCP,
          memory, vectors, permissions.
        </>,
        <>
          <strong>Self-hosting:</strong> <Link to="/docs/self-hosting">overview</Link> +{" "}
          <Link to="/docs/self-hosting/docker">Docker</Link>.
        </>,
      ]}
    />

    <h2>How the pieces fit together</h2>
    <p>
      Personal accounts only — one human, one set of projects. Each project bundles the tools, MCP
      servers, memory stores, and knowledge bases your agents need. Credentials (OAuth tokens, API
      keys) live one level up at the user so two projects can reuse the same Google account, but
      only the projects you explicitly grant.
    </p>
    <CodeBlock>{`User (you@example.com)
├── Credentials              # OAuth tokens, API keys (encrypted)
│   ├── google               # drive.readonly, gmail.readonly, ...
│   ├── github               # repo, issues
│   └── slack                # channels:history, chat:write
└── Projects
    ├── personal-assistant
    │   ├── Tools            # gmail.search, drive.find_doc, ...
    │   ├── MCP servers      # upstream + an exposed endpoint
    │   ├── Memory stores    # preferences, threads, recall
    │   ├── Vector indexes   # papers, transcripts
    │   ├── Knowledge bases  # docs/, drive/, websites
    │   └── Permissions      # claude can:read gmail, ...
    └── coding-workspace
        └── ... same shape per project ...`}</CodeBlock>

    <h2>Conventions you'll see across the docs</h2>
    <ProseList
      items={[
        <>
          <code>mk</code> is the SDK client (TS, Python, Go all use the same identifier in
          examples).
        </>,
        <>
          <code>mks_live_…</code> are project-scoped API keys. <code>mks_user_…</code> are
          user-scoped.
        </>,
        <>
          Every example assumes you've already created a project — see{" "}
          <Link to="/docs/first-project">Your first project</Link>.
        </>,
        <>
          We say "client" for the thing calling MemKernel (Claude Desktop, Cursor, your Node
          script). We say "agent" for the higher-level construct that may be made of one or more
          clients + a memory store + a schedule.
        </>,
      ]}
    />

    <h2>The wedge, made explicit</h2>
    <p>
      Most agent frameworks bury integrations inside their own SDK. So every time you swap models —
      Claude → GPT-4o → Mistral — or every time you spin up a second runtime — Cursor + your CRON
      job + a LangChain pipeline — you reauthorize, reauthor schemas, recopy handler code. MemKernel
      inverts that: integrations live in <em>one</em> place, your code asks for them in whatever
      shape it needs, and switching frameworks costs you nothing.
    </p>
  </>
);

const QuickstartBody = (
  <>
    <p>
      In five minutes you'll connect Google Drive once on MemKernel and expose it to Claude Desktop
      as an MCP server. From then on Claude can search, read, and link to your Drive without any
      custom code.
    </p>
    <p>
      <strong>You'll need:</strong> a Google account, Claude Desktop installed (1.0.6+), and three
      minutes of patience for the OAuth consent screen. Total wall-clock time is usually 4–6
      minutes; we time ourselves at <code>~4m 30s</code> from cold.
    </p>
    <h2>1. Create an account</h2>
    <QuickstartStep1Visual />
    <p>
      Head to <Link to="/signup">the sign-up page</Link> and either log in with Google / GitHub or
      sign up with an email + password. You'll land on your projects dashboard.
    </p>
    <h2>2. Create your first project</h2>
    <QuickstartStep2Visual />
    <p>
      Click <strong>New project</strong> and call it <code>personal-assistant</code>. A project is a
      bundle of tools, MCP servers, memory, and permissions that you'll expose to a specific agent
      (or family of agents).
    </p>
    <h2>3. Connect Google Drive</h2>
    <QuickstartStep3Visual />
    <p>
      In the project's <strong>Connectors</strong> tab, click <strong>Connect Google</strong> and
      approve the OAuth screen. We request <code>drive.readonly</code> by default — you can edit the
      scope set later.
    </p>
    <h2>4. Copy the MCP URL</h2>
    <QuickstartStep4Visual />
    <p>
      In the project's <strong>Expose</strong> tab, copy the MCP URL — it looks like{" "}
      <code>https://mcp.memkernel.com/p/abc123</code>.
    </p>
    <h2>5. Paste it into Claude Desktop</h2>
    <QuickstartStep5Visual />
    <p>
      Edit Claude Desktop's{" "}
      <code>~/Library/Application Support/Claude/claude_desktop_config.json</code>:
    </p>
    <CodeBlock lang="json">{`{
  "mcpServers": {
    "memkernel-personal": {
      "url": "https://mcp.memkernel.com/p/abc123"
    }
  }
}`}</CodeBlock>
    <p>
      Restart Claude Desktop. Open a new chat. Ask "search my Drive for Q4 planning notes." Claude
      will use the <code>drive_search</code> tool to find them.
    </p>
    <h2>Verify it worked</h2>
    <p>In a new Claude conversation, try one of these prompts:</p>
    <ul>
      <li>
        <em>"List the five most recently modified files in my Drive."</em> — Claude should call{" "}
        <code>drive_list</code>.
      </li>
      <li>
        <em>"Find the doc called Q4 planning and summarize the top three bullets."</em> — Claude
        calls <code>drive_search</code> then <code>drive_read</code>.
      </li>
    </ul>
    <p>
      If Claude says "I don't have access to your Drive" the MCP server didn't register — see
      Troubleshooting below.
    </p>

    <h2>Troubleshooting</h2>
    <dl>
      <dt>
        <strong>Claude doesn't show MemKernel tools.</strong>
      </dt>
      <dd>
        Restart Claude Desktop fully (Cmd+Q, not just close the window). Check Settings → Developer
        → MCP servers; <code>memkernel</code> should be in the list. Common cause: invalid JSON in
        the config. Validate with <code>jq . claude_desktop_config.json</code>.
      </dd>
      <dt>
        <strong>OAuth screen rejects you with a 400.</strong>
      </dt>
      <dd>
        You probably tried to grant a Google Workspace account where your admin has third-party app
        access disabled. Use a personal Google account, or have your Workspace admin allow the
        MemKernel OAuth client.
      </dd>
      <dt>
        <strong>Tool calls return 401.</strong>
      </dt>
      <dd>
        Your Google access token expired and the refresh failed. Open the project's{" "}
        <strong>Connectors</strong> tab and click <strong>Reconnect</strong>.
      </dd>
    </dl>

    <h2>What's next?</h2>
    <ProseList
      items={[
        <Link to="/docs/concepts/projects">Projects</Link>,
        <Link to="/docs/concepts/tools">Tools</Link>,
        <Link to="/docs/concepts/permissions">Permissions</Link>,
        <Link to="/docs/sdks/typescript">TypeScript SDK</Link>,
        <Link to="/docs/api/mcp">MCP API reference</Link>,
      ]}
    />
  </>
);

const InstallationBody = (
  <>
    <p>
      MemKernel is a hosted service first. Sign up at <Link to="/signup">memkernel.com</Link> and
      you're done — there's nothing to install for the dashboard. You only install something when
      you want to <em>call</em> MemKernel from your own code or
      <em>connect</em> an MCP client to it.
    </p>

    <h2>Requirements</h2>
    <ul>
      <li>
        <strong>TypeScript SDK:</strong> Node 20+, Bun 1.2+, Deno 1.40+, or modern browsers (with a
        proxy — we'll never expose your
        <code>MK_API_KEY</code> in client code).
      </li>
      <li>
        <strong>Python SDK:</strong> CPython 3.10+, asyncio recommended.
      </li>
      <li>
        <strong>Go SDK:</strong> Go 1.22+, generics-aware handlers.
      </li>
      <li>
        <strong>MCP clients:</strong> Claude Desktop 1.0.6+, Cursor 0.43+, Windsurf 1.10+, Open
        WebUI 0.5+, or any client implementing the MCP HTTP-Streamable transport.
      </li>
      <li>
        <strong>Self-host:</strong> Docker 24+, PostgreSQL 16+, a TLS-terminated reverse proxy. See{" "}
        <Link to="/docs/self-hosting">Self-hosting</Link>.
      </li>
    </ul>

    <h2>SDK install</h2>
    <CodeBlock lang="bash">{`# TypeScript / Node
pnpm add @memkernel/sdk
# (npm i @memkernel/sdk or yarn add @memkernel/sdk also fine)

# Python
pip install memkernel
# or: poetry add memkernel · uv add memkernel

# Go
go get github.com/memkernel/sdk-go`}</CodeBlock>

    <h2>Create + scope an API key</h2>
    <p>
      In the dashboard go to <strong>Settings → API keys → New key</strong>. You'll be asked to
      choose a scope:
    </p>
    <ul>
      <li>
        <strong>Project key</strong> — can read/write one project only. Use for production deploys
        (one key per service per project).
      </li>
      <li>
        <strong>User key</strong> — can act across all your projects. Use for one-off scripts; do
        not embed in deploys.
      </li>
    </ul>
    <p>
      Keys are shown <em>once</em>. Save them in your secret manager; we can't recover them.
    </p>

    <h2>Environment variables we read</h2>
    <CodeBlock lang="bash">{`# Required
export MK_API_KEY=mks_live_xxxxxxxxxxxx

# Optional (defaults shown)
export MK_BASE_URL=https://api.memkernel.com   # self-hosted? point here
export MK_PROJECT=personal-assistant            # default project
export MK_TIMEOUT_MS=30000                      # per-call HTTP timeout`}</CodeBlock>

    <h2>Verify the install</h2>
    <CodeBlock lang="ts">{`import { memkernel } from "@memkernel/sdk";

const mk = memkernel({ apiKey: process.env.MK_API_KEY! });

const me = await mk.users.me();
const projects = await mk.projects.list();

console.log(\`Signed in as \${me.email} — \${projects.length} projects\`);`}</CodeBlock>

    <h2>Troubleshooting</h2>
    <dl>
      <dt>
        <code>401 invalid_api_key</code>
      </dt>
      <dd>
        Either the key is wrong, or you copied a key prefix without the secret half. Regenerate from
        the dashboard.
      </dd>
      <dt>
        <code>403 project_not_in_scope</code>
      </dt>
      <dd>
        You used a Project key for one project to read another. Switch to a User key, or create a
        separate Project key per project.
      </dd>
      <dt>
        <code>ECONNREFUSED</code> against self-host
      </dt>
      <dd>
        <code>MK_BASE_URL</code> is missing or wrong — verify by hitting{" "}
        <code>$MK_BASE_URL/health/ready</code> from your shell first.
      </dd>
    </dl>
  </>
);

const FirstProjectBody = (
  <>
    <p>
      Projects are the unit of scope in MemKernel. Each one bundles its own tools, MCP servers,
      memory stores, and permissions. Most users have 2–4 — Personal Assistant, Coding, Research,
      Customer Support — each tuned to one workflow.
    </p>

    <h2>Pick the right number of projects</h2>
    <p>
      Spin up a new project whenever <em>any one</em> of these is true:
    </p>
    <ul>
      <li>
        the agent should expose a different loadout (e.g. coding agent gets GitHub + Linear;
        personal assistant gets Gmail + Calendar),
      </li>
      <li>
        the memory should not bleed across (your research notes shouldn't surface in customer
        support replies),
      </li>
      <li>
        you want a separate API key blast-radius (compromised key only affects one workspace), or
      </li>
      <li>you want to point a separate MCP URL at a different Claude / Cursor instance.</li>
    </ul>
    <p>
      Don't over-split. One project per "agent persona" is the sweet spot; one project per{" "}
      <em>tool</em> is too granular.
    </p>

    <h2>Anatomy of a project</h2>
    <CodeBlock lang="ts">{`Project {
  id:           string;             // pk_01H...
  slug:         string;             // unique per user
  name:         string;
  description?: string;
  createdAt:    string;             // ISO 8601
  updatedAt:    string;

  // Bundles inside the project
  tools:          Tool[];
  mcpServers:     McpServer[];      // both upstream + the exposed endpoint
  memoryStores:   MemoryStore[];
  vectorIndexes:  VectorIndex[];
  knowledgeBases: KnowledgeBase[];
  permissions:    PermissionRule[];
  apiKeys:        ApiKey[];         // project-scoped only
}`}</CodeBlock>

    <h2>Create one</h2>
    <CodeBlock lang="ts">{`const project = await mk.projects.create({
  slug: "coding",                     // [a-z0-9-], unique per user
  name: "Coding Workspace",
  description: "Tools for my IDE-resident agent.",
});`}</CodeBlock>
    <p>
      <strong>Slug rules.</strong> Lowercase letters, numbers, hyphens. Max 48 chars. Once created
      the slug is permanent — for renames, update <code>name</code> instead.
    </p>

    <h2>Expose it</h2>
    <p>
      Every project gets two stable URLs the moment it's created. You don't need to call any
      "expose" endpoint.
    </p>
    <CodeBlock lang="ts">{`const exp = await mk.projects.endpoints("coding");
console.log(exp.mcp);   // → https://mcp.memkernel.com/p/coding
console.log(exp.rest);  // → https://api.memkernel.com/v1/projects/coding

// Rotate the MCP bearer (revokes old clients):
await mk.projects.rotateMcpToken("coding");`}</CodeBlock>

    <h2>Connect a tool</h2>
    <p>
      Tools are connected via the dashboard's <strong>Connectors</strong> tab (preferred) or
      programmatically using a stored credential. The typical first connector for a new project is
      the matching OAuth provider:
    </p>
    <CodeBlock lang="ts">{`// Assumes you've already linked "google" under Settings → Credentials.
await mk.tools.installFromConnector({
  project: "coding",
  connector: "google",
  scopes:    ["drive.readonly", "gmail.readonly"],
});`}</CodeBlock>

    <h2>Inspect what's in the project</h2>
    <CodeBlock lang="ts">{`const summary = await mk.projects.summary("coding");
//  → { tools: 12, mcpServers: 1, memoryStores: 2, vectorIndexes: 1,
//      knowledgeBases: 0, callsLast24h: 384, ... }`}</CodeBlock>

    <h2>Where to next?</h2>
    <ProseList
      items={[
        <Link to="/docs/concepts/tools">Concept: Tools</Link>,
        <Link to="/docs/concepts/mcp">Concept: MCP servers</Link>,
        <Link to="/docs/concepts/permissions">Concept: Permissions</Link>,
        <Link to="/docs/api/rest">REST endpoint reference</Link>,
      ]}
    />
  </>
);

const ProjectsConceptBody = (
  <>
    <p>
      A project is the central organizational unit in MemKernel. Everything else — tools, MCP
      servers, memory stores, knowledge bases, permissions — lives <em>inside</em> a project. Pick
      your slug carefully; everything downstream references it.
    </p>

    <h2>Why projects exist</h2>
    <p>
      A single MemKernel account usually drives multiple distinct agents. Your coding agent needs
      GitHub + Linear + Postgres but <em>not</em> Gmail. Your support agent needs Zendesk + Stripe.
      Bundling them into projects keeps the blast radius small and the tool palette small enough
      that the model doesn't waste tokens reasoning over 80 tools.
    </p>
    <p>
      A good rule of thumb: <strong>10–25 tools per project max</strong>. Above ~30 you'll start to
      see model accuracy fall off because the tool list takes too many tokens.
    </p>

    <h2>What's project-scoped vs user-scoped</h2>
    <table>
      <thead>
        <tr>
          <th>Resource</th>
          <th>Scope</th>
          <th>Why</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Tools</td>
          <td>Project</td>
          <td>So the loadout matches the agent persona.</td>
        </tr>
        <tr>
          <td>MCP server endpoint</td>
          <td>Project</td>
          <td>One URL per client install.</td>
        </tr>
        <tr>
          <td>Memory stores + vector indexes</td>
          <td>Project</td>
          <td>Avoids cross-context bleed.</td>
        </tr>
        <tr>
          <td>Knowledge bases</td>
          <td>Project</td>
          <td>Each project ingests its own corpus.</td>
        </tr>
        <tr>
          <td>Permission rules</td>
          <td>Project</td>
          <td>"Claude reads Gmail" only applies in one project.</td>
        </tr>
        <tr>
          <td>Project API keys</td>
          <td>Project</td>
          <td>One compromised key affects one project.</td>
        </tr>
        <tr>
          <td>OAuth credentials</td>
          <td>User</td>
          <td>Reuse one Google account across projects.</td>
        </tr>
        <tr>
          <td>User API keys</td>
          <td>User</td>
          <td>Cross-project admin scripts.</td>
        </tr>
        <tr>
          <td>Billing</td>
          <td>User</td>
          <td>One subscription, all projects.</td>
        </tr>
      </tbody>
    </table>

    <h2>Cross-project sharing</h2>
    <p>
      Sometimes you want two projects to share a single resource — e.g. one corpus of research docs
      that both the research and coding agent can query. Use a <strong>shared memory store</strong>:
    </p>
    <CodeBlock lang="ts">{`await mk.memory.share({
  store:    "research-docs",   // lives in 'research' project
  withProject: "coding",       // grant read-only access to 'coding'
  mode:    "read",
});`}</CodeBlock>
    <p>
      Shared stores are read-only in the recipient project by default. To allow writes, pass{" "}
      <code>mode: "read-write"</code>.
    </p>

    <h2>Example projects</h2>
    <CodeBlock lang="json">{`[
  {
    "slug": "personal-assistant",
    "name": "Personal Assistant",
    "tools":  ["gmail.*", "drive.search", "calendar.*", "notion.read_page"],
    "memory": ["preferences", "active-threads"],
    "client": "Claude Desktop"
  },
  {
    "slug": "coding",
    "name": "Coding Workspace",
    "tools":  ["github.*", "linear.*", "sentry.search_event", "postgres.read"],
    "memory": ["code-review-history"],
    "client": "Cursor + a CRON job"
  },
  {
    "slug": "support",
    "name": "Customer Support",
    "tools":  ["zendesk.*", "stripe.read_customer", "notion.read_page"],
    "memory": ["macros", "common-issues"],
    "client": "An OpenAI-shaped fine-tune"
  }
]`}</CodeBlock>

    <h2>Lifecycle</h2>
    <p>
      Projects can be soft-deleted but the slug is reserved for 30 days so nobody can hijack the MCP
      URL. Hard-delete via <code>DELETE /v1/projects/&lt;slug&gt;?purge=true</code> drops everything
      (tools, memory, vectors, audit logs). There's no undo.
    </p>
  </>
);

const ToolsConceptBody = (
  <>
    <p>
      A <strong>tool</strong> is a typed function an agent can call. In MemKernel a tool lives in
      the registry once and shows up in five shapes simultaneously: MCP Tool, OpenAI function-call,
      Anthropic Tool, LangChain Tool, Mastra Tool, and a typed REST endpoint. You author the schema
      once; we keep the shapes in sync.
    </p>

    <h2>Anatomy</h2>
    <CodeBlock lang="ts">{`Tool {
  id:          string;          // tl_01H...
  project:     string;          // project slug
  name:        string;          // "<namespace>.<verb>" — e.g. gmail.search
  description: string;          // shown to the model
  inputSchema: JsonSchema;      // we accept zod / pydantic / raw too
  outputSchema?: JsonSchema;
  handler:     ToolHandler;     // builtin connector ref OR a custom fn
  permissions?: PermissionRule[];
  rateLimit?:  { window: "1m" | "1h"; max: number };
  idempotency?: { keyParam: string };  // dedupe on this input field
  revision:    number;          // bumped on every schema change
  deprecated?: { since: string; replacement?: string };
}`}</CodeBlock>

    <h2>Three kinds of tools</h2>
    <ol>
      <li>
        <strong>Connector tools.</strong> Generated when you connect an OAuth or API-key provider.{" "}
        <code>google.drive.search</code>, <code>github.issues.create</code>,{" "}
        <code>stripe.read_customer</code>. Always start here — they're maintained for you.
      </li>
      <li>
        <strong>Custom tools.</strong> You write the handler (TS, Python, Go) and we host it. Use
        for proprietary APIs, business logic, or composite calls that hit 2+ upstream providers.
      </li>
      <li>
        <strong>MCP-proxy tools.</strong> Imported from an upstream MCP server you've registered.
        Shows up alongside the rest of the project's tools.
      </li>
    </ol>

    <h2>Naming convention</h2>
    <p>
      Names are <code>&lt;namespace&gt;.&lt;verb&gt;</code> in snake-case. Built-ins use the
      provider as namespace: <code>gmail.search</code>, <code>github.create_issue</code>. For custom
      tools, prefix with your project slug: <code>coding.find_issue</code>,{" "}
      <code>research.summarize_pdf</code>. Don't shadow built-in names.
    </p>

    <h2>Define a custom tool</h2>
    <CodeBlock lang="ts">{`await mk.tools.create({
  project: "personal-assistant",
  name:    "find_recent_emails",
  description:
    "Find emails received in the last N days, optionally from one sender.",
  inputSchema: z.object({
    days: z.number().int().min(1).max(30),
    from: z.string().email().optional(),
  }),
  outputSchema: z.array(
    z.object({
      id:      z.string(),
      subject: z.string(),
      from:    z.string(),
      receivedAt: z.string(),  // ISO 8601
    }),
  ),
  rateLimit:  { window: "1m", max: 60 },
  idempotency: { keyParam: "from" },
  handler: async ({ days, from }, ctx) => {
    const q = \`newer_than:\${days}d\${from ? \` from:\${from}\` : ""}\`;
    return ctx.providers.google.gmail.list({ q });
  },
});`}</CodeBlock>

    <h2>Input/output validation</h2>
    <p>
      We run input validation server-side before the handler ever sees the call. Bad arguments get a
      structured error returned to the agent so the model can self-correct on the next turn:
    </p>
    <CodeBlock lang="json">{`{
  "error": {
    "code": "tool_input_invalid",
    "field": "days",
    "message": "Expected number between 1 and 30; received 92.",
    "schema_pointer": "#/properties/days/maximum"
  }
}`}</CodeBlock>

    <h2>
      Handler context (<code>ctx</code>)
    </h2>
    <p>
      Every handler receives a <code>ctx</code> with:
    </p>
    <ul>
      <li>
        <code>ctx.providers</code> — already-authenticated client objects for every credential the
        project has, e.g. <code>ctx.providers.google.drive</code>. No token plumbing.
      </li>
      <li>
        <code>ctx.memory</code> — read/write into the project's memory stores.
      </li>
      <li>
        <code>ctx.vectors</code> — semantic search across the project's vector indexes.
      </li>
      <li>
        <code>ctx.caller</code> — <code>{`{ kind: "client" | "agent", id, name }`}</code> — useful
        for per-client permission checks inside the handler.
      </li>
      <li>
        <code>ctx.requestId</code> — tracing id, also surfaced in the audit log.
      </li>
    </ul>

    <h2>Errors, retries, idempotency</h2>
    <ul>
      <li>
        <strong>Retries.</strong> 5xx and network errors are retried automatically with jitter, up
        to 3 attempts. Set <code>retry: false</code> in <code>tools.create</code> to opt out (e.g.
        destructive operations).
      </li>
      <li>
        <strong>Idempotency.</strong> Setting <code>idempotency.keyParam</code> means MemKernel
        dedupes calls with the same value of that field within a 24h window. Useful for "create X if
        not exists" tools.
      </li>
      <li>
        <strong>Errors.</strong> Throw a typed error with{" "}
        <code>{`ctx.error("upstream_unavailable", { retryable: true })`}</code> and the SDK turns it
        into a structured error response.
      </li>
    </ul>

    <p>
      See <Link to="/docs/tools">the tool registry</Link> for management and{" "}
      <Link to="/docs/tools/export">multi-protocol export</Link> for how the same definition becomes
      Anthropic / OpenAI / LangChain.
    </p>
  </>
);

const McpConceptBody = (
  <>
    <p>
      MCP — the{" "}
      <a href="https://modelcontextprotocol.io" target="_blank" rel="noreferrer">
        Model Context Protocol
      </a>{" "}
      — is the open standard agent clients use to discover and call tools. MemKernel speaks MCP in
      both directions: it exposes your projects <em>as</em> MCP servers, and it can register other
      MCP servers as <em>upstreams</em> whose tools join the project.
    </p>

    <h2>Two directions, one protocol</h2>
    <ProseList
      items={[
        <>
          <strong>Exposed</strong> — every project automatically has an MCP endpoint at{" "}
          <code>mcp.memkernel.com/p/&lt;slug&gt;</code>. Drop the URL into Claude Desktop / Cursor /
          Windsurf / Open WebUI; the project's whole loadout becomes available.
        </>,
        <>
          <strong>Upstream</strong> — you register external MCP servers (e.g. the official Linear
          MCP, or your own internal MCP). Their tools get namespaced under the upstream's name and
          show up alongside your built-in tools.
        </>,
      ]}
    />

    <h2>Transports we support</h2>
    <table>
      <thead>
        <tr>
          <th>Transport</th>
          <th>Exposed</th>
          <th>Upstream</th>
          <th>When to use</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <strong>HTTP Streamable</strong>
          </td>
          <td>✓ (default)</td>
          <td>✓</td>
          <td>The only sensible choice for hosted; pick this.</td>
        </tr>
        <tr>
          <td>SSE</td>
          <td>—</td>
          <td>✓ (legacy)</td>
          <td>Compat only — deprecated in MCP 0.4+.</td>
        </tr>
        <tr>
          <td>stdio</td>
          <td>—</td>
          <td>✓</td>
          <td>For locally-runnable MCP servers (e.g. our CLI).</td>
        </tr>
      </tbody>
    </table>

    <h2>Client config — Claude Desktop</h2>
    <p>
      Edit <code>~/Library/Application Support/Claude/claude_desktop_config.json</code> (macOS) or{" "}
      <code>%APPDATA%\\Claude\\claude_desktop_config.json</code> (Windows):
    </p>
    <CodeBlock lang="json">{`{
  "mcpServers": {
    "memkernel-personal": {
      "url": "https://mcp.memkernel.com/p/personal-assistant",
      "auth": {
        "kind": "bearer",
        "token": "mks_mcp_xxxxxxxxxxxx"
      }
    }
  }
}`}</CodeBlock>
    <p>Restart Claude Desktop fully (Cmd+Q) for the change to register.</p>

    <h2>Client config — Cursor</h2>
    <p>
      Open <code>Settings → Tools → MCP servers</code> and add:
    </p>
    <CodeBlock lang="json">{`{
  "mcp.servers": {
    "memkernel-coding": {
      "url": "https://mcp.memkernel.com/p/coding-workspace",
      "headers": {
        "Authorization": "Bearer mks_mcp_xxxxxxxxxxxx"
      }
    }
  }
}`}</CodeBlock>

    <h2>Register an upstream MCP server</h2>
    <CodeBlock lang="ts">{`await mk.mcp.register({
  project:   "coding",
  name:      "linear",                  // becomes the namespace prefix
  transport: "http",
  url:       "https://mcp.linear.app",
  auth:      { kind: "oauth", credential: "linear-oauth" },
  // Optional: cap which tools from the upstream to expose
  toolAllowlist: ["linear.create_issue", "linear.search_issues"],
});`}</CodeBlock>
    <p>
      Tool calls now route through MemKernel: client → <code>memkernel</code> →{" "}
      <code>linear.app</code> → response cached + audit-logged → client.
    </p>

    <h2>Health, retries, and circuit breaking</h2>
    <p>
      Every registered upstream is health-checked every 30 seconds. If an upstream returns 5xx for 5
      consecutive checks the breaker opens for 60s and we return <code>upstream_unavailable</code>{" "}
      immediately instead of hanging the agent.
    </p>

    <h2>Rotating the MCP token</h2>
    <p>
      The bearer token a client uses to authenticate against your MCP URL is independent of your API
      keys. Rotate it from the dashboard or:
    </p>
    <CodeBlock lang="bash">{`curl -X POST https://api.memkernel.com/v1/projects/coding/mcp/rotate \\
  -H "Authorization: Bearer mks_user_..."`}</CodeBlock>
    <p>Old tokens stop working immediately. Update the client config and restart it.</p>
  </>
);

const MemoryConceptBody = (
  <>
    <p>
      Memory is persistent context an agent can read and write across conversations. Without it,
      every chat is amnesiac and the user ends up re-typing the same preferences, the same project
      state, and the same "by the way I'm in Berlin and prefer terse replies."
    </p>

    <h2>Four scopes</h2>
    <table>
      <thead>
        <tr>
          <th>Scope</th>
          <th>Lifetime</th>
          <th>Visible to</th>
          <th>Typical contents</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <strong>User</strong>
          </td>
          <td>Forever (or until you delete)</td>
          <td>All projects you own</td>
          <td>Tone, timezone, communication style, accounts you own</td>
        </tr>
        <tr>
          <td>
            <strong>Project</strong>
          </td>
          <td>Forever, project-scoped</td>
          <td>Anything in that project</td>
          <td>Coding conventions, customers, internal naming, project state</td>
        </tr>
        <tr>
          <td>
            <strong>Agent / session</strong>
          </td>
          <td>Until the session ends (default 30d)</td>
          <td>One agent instance</td>
          <td>Scratchpad, mid-task notes, this conversation's "what we just decided"</td>
        </tr>
        <tr>
          <td>
            <strong>Shared</strong>
          </td>
          <td>Configurable per store</td>
          <td>Multiple agents you opt-in</td>
          <td>Cross-agent collaboration — "the support agent learned X; tell the sales agent"</td>
        </tr>
      </tbody>
    </table>

    <h2>What memory stores look like</h2>
    <p>
      Each store is a typed key→value collection (think Redis but durable + semantically-indexed).
      Stores have a JSON Schema so writes get validated; without one, MemKernel infers a schema
      after ~10 writes.
    </p>
    <CodeBlock lang="ts">{`MemoryStore {
  id:        string;           // ms_01H...
  project:   string;           // project slug
  scope:     "user" | "project" | "session" | "shared";
  name:      string;
  schema?:   JsonSchema;       // optional but recommended
  semantic:  boolean;          // build a vector index over values
  embeddingModel?: string;     // when semantic: true
  ttl?:      string;           // e.g. "30d" → auto-expire
}`}</CodeBlock>

    <h2>Write and recall</h2>
    <CodeBlock lang="ts">{`// Write a single value
await mk.memory.set({
  store: "preferences",        // a project store
  key:   "tone",
  value: "terse, no fluff, code blocks > prose",
});

// Bulk write
await mk.memory.upsert({
  store: "active-threads",
  items: [
    { key: "thread:42", value: { topic: "Q4 planning", status: "open" } },
    { key: "thread:43", value: { topic: "compliance review", status: "blocked" } },
  ],
});

// Exact key lookup
const tone = await mk.memory.get("preferences", "tone");

// Semantic recall (requires semantic: true on the store)
const hits = await mk.memory.recall({
  store: "active-threads",
  query: "what threads were we discussing about end-of-year?",
  k:     5,
  filter: { status: "open" },
});`}</CodeBlock>

    <h2>How agents see memory</h2>
    <p>
      In every tool handler, <code>ctx.memory.recall(...)</code> is one line away. In an MCP client
      like Claude Desktop, MemKernel auto-injects relevant memory hits into the conversation as
      system messages — the model literally <em>sees</em> them as context.
    </p>
    <p>
      You can disable auto-injection per project (Settings → Memory → Auto-inject) if you'd rather
      control the prompt context manually.
    </p>

    <h2>Forgetting</h2>
    <p>Memory should be forgettable. We expose:</p>
    <ul>
      <li>
        <code>mk.memory.delete(store, key)</code> — drop one entry.
      </li>
      <li>
        <code>mk.memory.forget(store, query)</code> — semantic forgetting; deletes the top-k hits
        matching the query (great for "the user just told me to forget their old preference").
      </li>
      <li>
        <code>mk.memory.dropStore(store)</code> — nuke the whole store.
      </li>
    </ul>
    <p>
      Deletions are hard by default and not recoverable. Use <code>{`{ soft: true }`}</code> for a
      30-day tombstone you can restore.
    </p>

    <p>
      See <Link to="/docs/memory/vector">Vector & RAG</Link> for how semantic recall is backed.
    </p>
  </>
);

const VectorConceptBody = (
  <>
    <p>
      MemKernel ships a unified vector layer. Pick a backend per project; we handle embeddings,
      chunking, similarity search, and metadata filtering. The same SDK call works regardless of
      whether the index lives in pgvector or Pinecone or your own Qdrant.
    </p>

    <h2>Pick a backend</h2>
    <table>
      <thead>
        <tr>
          <th>Backend</th>
          <th>Best for</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <strong>pgvector</strong> (built-in)
          </td>
          <td>Most use cases</td>
          <td>No extra service. Up to ~1M vectors per project before you'll want to migrate.</td>
        </tr>
        <tr>
          <td>
            <strong>Qdrant</strong>
          </td>
          <td>Self-hosters at scale</td>
          <td>Open-source, payloads + filtering, recommended above 1M vectors on-prem.</td>
        </tr>
        <tr>
          <td>
            <strong>Pinecone</strong>
          </td>
          <td>Managed, no ops</td>
          <td>Serverless tier auto-scales; bring your own API key.</td>
        </tr>
        <tr>
          <td>
            <strong>Weaviate</strong>
          </td>
          <td>Hybrid keyword + vector</td>
          <td>Useful when you need BM25 sidecar.</td>
        </tr>
        <tr>
          <td>
            <strong>Chroma</strong>
          </td>
          <td>Local dev / hobby</td>
          <td>Single-process; not recommended for production.</td>
        </tr>
      </tbody>
    </table>

    <h2>Pick an embedding model</h2>
    <table>
      <thead>
        <tr>
          <th>Model</th>
          <th>Dim</th>
          <th>Strength</th>
          <th>Cost / 1M tokens</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>openai/text-embedding-3-small</code>
          </td>
          <td>1536</td>
          <td>Default; best speed/cost balance</td>
          <td>$0.02</td>
        </tr>
        <tr>
          <td>
            <code>openai/text-embedding-3-large</code>
          </td>
          <td>3072</td>
          <td>Highest recall</td>
          <td>$0.13</td>
        </tr>
        <tr>
          <td>
            <code>voyage/voyage-3</code>
          </td>
          <td>1024</td>
          <td>Strong on code + technical docs</td>
          <td>$0.06</td>
        </tr>
        <tr>
          <td>
            <code>cohere/embed-multilingual-v3</code>
          </td>
          <td>1024</td>
          <td>100+ languages</td>
          <td>$0.10</td>
        </tr>
        <tr>
          <td>
            <code>jina/embeddings-v3</code>
          </td>
          <td>1024</td>
          <td>Open-weights, late chunking</td>
          <td>$0.02</td>
        </tr>
        <tr>
          <td>
            <code>local/bge-large-en-v1.5</code>
          </td>
          <td>1024</td>
          <td>Self-host, zero $</td>
          <td>—</td>
        </tr>
      </tbody>
    </table>
    <p>
      Switching models requires a re-embed. We do it in the background; old vectors keep serving
      until the new ones are ready, then we atomically swap.
    </p>

    <h2>Chunking</h2>
    <p>Three strategies are supported when ingesting documents:</p>
    <ul>
      <li>
        <code>fixed</code> — every <code>N</code> tokens (default 512, overlap 64). Predictable; bad
        for paragraph-aware retrieval.
      </li>
      <li>
        <code>semantic</code> — split on sentence/paragraph boundaries up to <code>maxTokens</code>.
        Recommended default.
      </li>
      <li>
        <code>recursive</code> — try heading → paragraph → sentence; good for code + Markdown.
      </li>
    </ul>

    <h2>Use it</h2>
    <CodeBlock lang="ts">{`// Upsert raw text + metadata
await mk.vectors.upsert({
  index: "research-papers",
  docs: [
    {
      id:   "p1",
      text: "The MemKernel paper introduces a unified registry ...",
      metadata: { year: 2026, author: "anthropic", tags: ["control-plane"] },
    },
  ],
});

// Similarity search with metadata filter
const hits = await mk.vectors.search({
  index: "research-papers",
  query: "how does memkernel route mcp calls",
  k:     8,
  filter: {
    year: { $gte: 2025 },
    tags: { $in: ["control-plane", "agents"] },
  },
});
// → [{ id, score, text, metadata }, ...]`}</CodeBlock>

    <h2>Hybrid search</h2>
    <p>
      Pure vector is bad at proper nouns and exact-string match. Turn on hybrid retrieval to combine
      cosine similarity with BM25; we tune the weights per index based on click-through if you're
      using MemKernel-as-a-search-backend.
    </p>
    <CodeBlock lang="ts">{`await mk.vectors.update("research-papers", {
  hybrid: { mode: "rrf", weights: { vector: 0.7, bm25: 0.3 } },
});`}</CodeBlock>

    <h2>RAG mode</h2>
    <p>
      Skip the hand-rolled retrieve→prompt→generate loop with <code>mk.vectors.rag({})</code> — it
      does retrieval + reranking + grounded generation in one call, using the model of your choice:
    </p>
    <CodeBlock lang="ts">{`const reply = await mk.vectors.rag({
  index: "research-papers",
  query: "summarize the MemKernel routing model",
  model: "anthropic:claude-sonnet-4-6",
  rerank: { model: "voyage/rerank-2", topK: 5 },
});
// → { answer, citations: [{ id, score, snippet }, ...] }`}</CodeBlock>
  </>
);

const PermissionsConceptBody = (
  <>
    <p>
      Permissions in MemKernel decide what each client can do inside a project. The model is
      intentionally small (you can read the rule grammar on one page) but expressive enough to
      encode real-world least-privilege requirements.
    </p>

    <h2>The rule grammar</h2>
    <CodeBlock lang="ts">{`PermissionRule {
  subject: string;             // "client:claude", "client:cursor",
                               //  "agent:research-bot", "*"
  target:  string;             // "tool:gmail.search", "tool:gmail.*",
                               //  "store:preferences", "*"
  action:  "call" | "read" | "write" | "*";
  scope?:  Record<string, JsonValue>;  // arg-level restrictions
  effect:  "allow" | "deny";   // deny wins over allow
  reason?: string;             // shown in audit log
}`}</CodeBlock>

    <h2>Evaluation</h2>
    <ol>
      <li>
        Default is <strong>deny</strong> — nothing is callable until rules say so.
      </li>
      <li>Rules with more specific subject + target win over wildcards.</li>
      <li>
        <code>deny</code> always beats <code>allow</code> at the same specificity (useful for
        "Claude can do everything except send emails").
      </li>
      <li>
        <code>scope</code> is evaluated on the tool's input arguments at call time, not at
        definition time.
      </li>
    </ol>

    <h2>Examples</h2>
    <p>
      <strong>Claude can read Gmail + create calendar events.</strong>
    </p>
    <CodeBlock lang="json">{`[
  { "subject": "client:claude", "target": "tool:gmail.search",          "action": "call", "effect": "allow" },
  { "subject": "client:claude", "target": "tool:gmail.read_message",    "action": "call", "effect": "allow" },
  { "subject": "client:claude", "target": "tool:calendar.create_event", "action": "call", "effect": "allow" }
]`}</CodeBlock>

    <p>
      <strong>Cursor can do anything in this project.</strong>
    </p>
    <CodeBlock lang="json">{`[
  { "subject": "client:cursor", "target": "*", "action": "*", "effect": "allow" }
]`}</CodeBlock>

    <p>
      <strong>Research agent can search Drive but never write,</strong> and can only read documents
      tagged <code>public</code>.
    </p>
    <CodeBlock lang="json">{`[
  { "subject": "agent:research-bot", "target": "tool:drive.search", "action": "call",
    "scope":   { "tags": { "$in": ["public"] } },
    "effect":  "allow" },
  { "subject": "agent:research-bot", "target": "store:*",           "action": "write",
    "effect":  "deny",  "reason": "research is read-only" }
]`}</CodeBlock>

    <p>
      <strong>
        Claude can email anyone, except no <code>sales@</code>
        addresses.
      </strong>
    </p>
    <CodeBlock lang="json">{`[
  { "subject": "client:claude", "target": "tool:gmail.send", "action": "call", "effect": "allow" },
  { "subject": "client:claude", "target": "tool:gmail.send", "action": "call",
    "scope":   { "to": { "$endsWith": "@sales.acme.com" } },
    "effect":  "deny", "reason": "no outbound to sales prospects" }
]`}</CodeBlock>

    <h2>Scope operators</h2>
    <p>
      <code>scope</code> uses a small subset of Mongo-style query operators: <code>$eq</code>,{" "}
      <code>$ne</code>, <code>$in</code>, <code>$nin</code>, <code>$gt</code>, <code>$gte</code>,{" "}
      <code>$lt</code>, <code>$lte</code>, <code>$startsWith</code>, <code>$endsWith</code>,{" "}
      <code>$matches</code> (regex), <code>$and</code>, <code>$or</code>, <code>$not</code>.
    </p>

    <h2>Identifying the caller</h2>
    <p>
      Each MCP client gets a unique caller identity the first time it connects (visible in Settings
      → Permissions → Clients). The same Claude Desktop install on the same machine keeps the same
      identity across restarts, so rules persist.
    </p>
    <p>For agents you write in code, set the identity explicitly:</p>
    <CodeBlock lang="ts">{`const mk = memkernel({
  apiKey: process.env.MK_API_KEY!,
  callerId: "agent:research-bot",   // shows up in audit + rule matching
});`}</CodeBlock>

    <h2>Audit logs</h2>
    <p>
      Every tool call is recorded with: caller, target, arguments (redacted by schema), result code,
      latency, and which rules matched. Inspect from the dashboard's <strong>Audit</strong> tab or
      programmatically:
    </p>
    <CodeBlock lang="ts">{`const events = await mk.audit.list({
  project: "personal-assistant",
  filter:  { effect: "deny", since: "2026-05-01" },
  limit:   100,
});`}</CodeBlock>
    <p>
      Audit logs are retained for 90 days by default; bump to 1 year on Pro (Settings → Audit →
      Retention).
    </p>
  </>
);

const ConnectorsCatalogBody = (
  <>
    <p>
      MemKernel ships with 310+ first-party connectors. Browse the{" "}
      <Link to="/integrations">full catalog</Link> — every connector generates a set of tools you
      can call from any agent runtime.
    </p>

    <h2>Categories</h2>
    <table>
      <thead>
        <tr>
          <th>Category</th>
          <th>Examples</th>
          <th>Auth</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>AI / ML</td>
          <td>OpenAI, Anthropic, Gemini, Mistral, Cohere, Replicate, HF</td>
          <td>API key</td>
        </tr>
        <tr>
          <td>Cloud</td>
          <td>AWS, GCP, Azure, Cloudflare, Vercel, Railway, Fly</td>
          <td>Mixed</td>
        </tr>
        <tr>
          <td>Email</td>
          <td>Gmail, Outlook, SendGrid, Resend, Postmark</td>
          <td>OAuth / API key</td>
        </tr>
        <tr>
          <td>Calendar</td>
          <td>Google Calendar, Outlook, Cal.com, Calendly</td>
          <td>OAuth</td>
        </tr>
        <tr>
          <td>Productivity</td>
          <td>Notion, Linear, Jira, Asana, Airtable, Coda</td>
          <td>OAuth</td>
        </tr>
        <tr>
          <td>Communication</td>
          <td>Slack, Discord, Microsoft Teams, Telegram, Twilio</td>
          <td>OAuth / API key</td>
        </tr>
        <tr>
          <td>CRM / Sales</td>
          <td>Salesforce, HubSpot, Pipedrive, Close, Apollo</td>
          <td>OAuth</td>
        </tr>
        <tr>
          <td>Support</td>
          <td>Zendesk, Intercom, Help Scout, Front</td>
          <td>OAuth / API key</td>
        </tr>
        <tr>
          <td>Payments</td>
          <td>Stripe, PayPal, Square, Adyen, Klarna</td>
          <td>API key</td>
        </tr>
        <tr>
          <td>Databases</td>
          <td>Postgres, MySQL, MongoDB, BigQuery, Snowflake</td>
          <td>Conn string / IAM</td>
        </tr>
        <tr>
          <td>Vector DBs</td>
          <td>Pinecone, Qdrant, Weaviate, Chroma</td>
          <td>API key</td>
        </tr>
        <tr>
          <td>Storage</td>
          <td>S3, GCS, Drive, OneDrive, Dropbox</td>
          <td>OAuth / IAM</td>
        </tr>
        <tr>
          <td>Dev tools</td>
          <td>GitHub, GitLab, Bitbucket, Sentry, Datadog, Grafana</td>
          <td>OAuth / API key</td>
        </tr>
      </tbody>
    </table>

    <h2>Lifecycle of a connection</h2>
    <ol>
      <li>
        You hit <strong>Connect</strong> on a provider card.
      </li>
      <li>
        OAuth: redirect → consent → callback. API key: paste, we verify with a no-op call before
        saving.
      </li>
      <li>
        Token + secrets are encrypted with AES-256-GCM. The encryption key is per-user, derived from
        a master key we hold (or that you hold, if you self-host).
      </li>
      <li>
        Tokens refresh automatically. OAuth refresh failures (revoked, expired permanently, scope
        changes) generate a dashboard notification and a webhook event.
      </li>
      <li>Tools generated by the connector appear in any project you grant access to.</li>
    </ol>

    <h2>Naming + scoping</h2>
    <p>
      You can have multiple credentials for the same provider — e.g. one personal Google account +
      one consulting client's Google account. Tag each with a label so the dashboard + audit log
      stays readable:
    </p>
    <CodeBlock lang="ts">{`await mk.credentials.create({
  provider: "google",
  label:    "personal",     // [a-z0-9-]
  scopes:   ["drive.readonly", "gmail.readonly"],
});

// Reference it explicitly in a tool config:
await mk.tools.installFromConnector({
  project:   "personal-assistant",
  connector: "google",
  credential:"personal",     // ← the label
});`}</CodeBlock>

    <h2>Don't see a provider?</h2>
    <p>
      Three paths: (1) open a request issue on{" "}
      <a href="https://github.com/cemsina/memkernel" target="_blank" rel="noreferrer">
        GitHub
      </a>{" "}
      — we ship one or two new connectors per week, (2) wrap the provider's HTTP API as a{" "}
      <Link to="/docs/connectors/custom">custom connector</Link>, or (3) if the provider already has
      an MCP server, register it as an <Link to="/docs/concepts/mcp">upstream MCP</Link>.
    </p>
  </>
);

const ConnectorsOauthBody = (
  <>
    <p>
      OAuth connectors give scoped access to upstream providers without your code ever handling the
      raw access token. MemKernel exchanges the auth code, encrypts the refresh token at rest, and
      rotates the access token automatically before each tool call.
    </p>

    <h2>The flow, end-to-end</h2>
    <ol>
      <li>
        You click <strong>Connect</strong> on a provider card.
      </li>
      <li>
        MemKernel builds the authorize URL with PKCE + a one-time state and 302s you to the
        provider's consent screen.
      </li>
      <li>
        On callback, the auth code is exchanged for an access + refresh token. We verify the granted
        scope set matches what was requested.
      </li>
      <li>
        Tokens are encrypted with AES-256-GCM under your user's envelope key and stored in Postgres.
        We log the encrypted-at time, never the token content.
      </li>
      <li>
        Tools generated by the granted scopes appear in any project you grant access to. Until a
        tool fires, the access token is never decrypted.
      </li>
    </ol>

    <h2>Scope sets per provider</h2>
    <p>
      Each provider has a <strong>default</strong> scope set (usually read-only) and an{" "}
      <strong>elevated</strong> set you can opt into per-project. Examples:
    </p>
    <CodeBlock lang="json">{`// google — default (read-only)
[
  "openid", "email", "profile",
  "https://www.googleapis.com/auth/drive.readonly",
  "https://www.googleapis.com/auth/calendar.readonly",
  "https://www.googleapis.com/auth/gmail.readonly"
]

// google — elevated (send email, create events, write drive files)
[
  "https://www.googleapis.com/auth/gmail.send",
  "https://www.googleapis.com/auth/calendar.events",
  "https://www.googleapis.com/auth/drive.file"
]

// github — default
["read:user", "user:email", "repo:status", "public_repo"]

// github — elevated
["repo", "admin:repo_hook", "workflow"]

// slack — default
["channels:history", "channels:read", "users:read"]

// slack — elevated
["chat:write", "files:write", "im:write", "groups:write"]`}</CodeBlock>

    <h2>Re-consenting and scope upgrades</h2>
    <p>
      Asking for a new scope after the user has already connected requires a re-consent. We surface
      this in the dashboard as a yellow pill on the connector card and short-circuit the relevant
      tools with <code>scope_not_granted</code> until the user re-consents.
    </p>

    <h2>Revocation</h2>
    <ul>
      <li>
        Revoking on MemKernel deletes the encrypted refresh token and immediately stops all tool
        calls (returns <code>credential_revoked</code>).
      </li>
      <li>
        Revoking on the provider side (e.g. you remove the app from Google's third-party apps list)
        is detected on the next refresh attempt; we mark the credential dead within ~60s.
      </li>
    </ul>

    <h2>Per-project credential routing</h2>
    <p>
      Credentials live at the user level but each project decides which credential to use. If you
      have <code>google:personal</code> and <code>google:consulting</code> connections, point a
      coding project at the latter:
    </p>
    <CodeBlock lang="ts">{`await mk.projects.setCredential({
  project:   "coding",
  provider:  "google",
  credential:"consulting",
});`}</CodeBlock>

    <p>
      See <Link to="/docs/concepts/permissions">Permissions</Link> for how scopes translate into
      per-client allowable actions.
    </p>
  </>
);

const ConnectorsApiKeysBody = (
  <>
    <p>
      Plenty of providers don't have OAuth — they hand out raw API keys. MemKernel encrypts those
      keys at rest, verifies them before saving, and never logs the secret content. Reads require an
      active user session and a recent re-auth (configurable).
    </p>

    <h2>Add a key</h2>
    <CodeBlock lang="ts">{`await mk.credentials.create({
  provider: "openai",
  kind:     "api_key",
  label:    "personal",
  secrets:  { apiKey: process.env.OPENAI_API_KEY! },
});`}</CodeBlock>
    <p>
      Before saving, we make a no-op call against the provider (e.g. <code>/v1/models</code> for
      OpenAI). If it 401s, the credential isn't stored.
    </p>

    <h2>Storage model</h2>
    <ul>
      <li>
        Secrets are encrypted with AES-256-GCM. Encryption key is per-user, wrapped by a master KMS
        key (or your local <code>ENCRYPTION_KEY</code> when self-hosting).
      </li>
      <li>
        The encrypted blob lives in Postgres; the master key never touches the application disk.
      </li>
      <li>
        Reading a secret via the API requires a fresh session (default TTL: 15 minutes; configurable
        per credential).
      </li>
      <li>
        Audit-log entries record every <em>read</em>, not just every use — so you can see when a
        script pulled the key.
      </li>
    </ul>

    <h2>Rotation</h2>
    <p>
      Rotating is a single call; we keep the old key valid for 5 minutes so in-flight requests don't
      fail:
    </p>
    <CodeBlock lang="ts">{`await mk.credentials.rotate({
  id: "cr_01H...",
  secrets: { apiKey: process.env.NEW_OPENAI_KEY! },
});`}</CodeBlock>

    <h2>Multiple secrets per credential</h2>
    <p>
      Some providers want more than one value (e.g. AWS needs both an access-key-id and a
      secret-access-key). Pass the whole object:
    </p>
    <CodeBlock lang="ts">{`await mk.credentials.create({
  provider: "aws",
  kind:     "api_key",
  label:    "production",
  secrets:  {
    accessKeyId:     process.env.AWS_ACCESS_KEY_ID!,
    secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY!,
    region:          "eu-central-1",
  },
});`}</CodeBlock>

    <h2>Bringing your own KMS</h2>
    <p>
      Enterprise + self-hosted setups can plug into AWS KMS, GCP KMS, or HashiCorp Vault as the
      master key store. The credential blob stored in Postgres becomes useless without your KMS, so
      even a full DB exfiltration doesn't expose secrets. See{" "}
      <Link to="/docs/self-hosting">Self-hosting</Link>.
    </p>
  </>
);

const ConnectorsCustomBody = (
  <>
    <p>
      Don't see your provider in the catalog? Custom connectors let you wrap any HTTP API, gRPC
      service, OpenAPI spec, or local script as a first-class MemKernel connector — with tool
      generation, auth-token storage, retries, rate limiting, and audit logging all handled for you.
    </p>

    <h2>HTTP from a JSON-Schema spec</h2>
    <CodeBlock lang="ts">{`await mk.connectors.create({
  kind:    "http",
  name:    "internal-billing-api",
  baseUrl: "https://billing.internal.acme.com",
  auth:    { kind: "bearer", token: process.env.BILLING_TOKEN! },
  tools: [
    {
      name:        "find_subscription",
      description: "Look up a subscription by customer email.",
      method:      "GET",
      path:        "/subscriptions/find",
      query: {
        email: { type: "string", format: "email" },
      },
      response: {
        type: "object",
        properties: {
          id:     { type: "string" },
          plan:   { type: "string" },
          status: { type: "string", enum: ["active", "cancelled", "past_due"] },
        },
        required: ["id"],
      },
    },
    {
      name:        "cancel_subscription",
      method:      "POST",
      path:        "/subscriptions/{id}/cancel",
      pathParams:  { id: { type: "string" } },
      retry:       false,         // destructive — don't retry
      permissions: [{ subject: "client:claude", effect: "deny", action: "call" }],
    },
  ],
});`}</CodeBlock>

    <h2>From an OpenAPI spec</h2>
    <p>
      If the provider publishes OpenAPI, point us at it and we'll generate the tools for you. By
      default we expose only the
      <code>GET</code> + <code>POST</code> operations; opt operations in/out per-name:
    </p>
    <CodeBlock lang="ts">{`await mk.connectors.fromOpenAPI({
  name:    "internal-billing-api",
  spec:    "https://billing.internal.acme.com/openapi.json",
  baseUrl: "https://billing.internal.acme.com",
  auth:    { kind: "bearer", env: "BILLING_TOKEN" },
  tools: {
    allow: ["find_subscription", "list_invoices"],
    deny:  ["delete_*"],
  },
});`}</CodeBlock>

    <h2>Custom code handler</h2>
    <p>
      When no upstream HTTP API will do — e.g. you want to consult a local DB, run a query, fall
      back to a second provider — write the handler in your SDK of choice:
    </p>
    <CodeBlock lang="ts">{`await mk.tools.create({
  project: "support",
  name:    "lookup_high_value_customer",
  description:
    "Return Stripe + CRM data for a customer if their ARR > $50k.",
  inputSchema: z.object({ email: z.string().email() }),
  handler: async ({ email }, ctx) => {
    const stripeCustomer = await ctx.providers.stripe.customers.search({
      query: \`email:'\${email}'\`,
    });
    if (!stripeCustomer) return null;

    const crmRecord = await ctx.providers.hubspot.contacts.get(
      stripeCustomer.metadata.hubspot_id,
    );
    const arr = Number(crmRecord.properties.annual_revenue ?? 0);
    if (arr < 50_000) return null;

    return { stripe: stripeCustomer, hubspot: crmRecord, arr };
  },
});`}</CodeBlock>

    <h2>MCP upstream as a connector</h2>
    <p>
      Any MCP server you don't control can still feed into MemKernel — register it as an upstream
      and its tools appear in your project namespaced under the chosen name. See{" "}
      <Link to="/docs/concepts/mcp">MCP servers</Link>.
    </p>

    <h2>Local / stdio handlers (self-host)</h2>
    <p>
      Self-host only: you can ship a handler binary alongside the MemKernel image and register it by
      path. Useful when the data lives behind a VPN MemKernel cloud can't reach.
    </p>
  </>
);

const ToolsRegistryBody = (
  <>
    <p>
      The tool registry is the source of truth for every tool in a project — Postgres-backed,
      indexed, audit-logged, and queryable over REST + the SDK. Everything else (MCP, OpenAI
      function shapes, LangChain tools) is generated from the rows in this table.
    </p>

    <h2>Listing + filtering</h2>
    <CodeBlock lang="ts">{`// Plain list
const tools = await mk.tools.list({ project: "coding" });

// With filters
const writes = await mk.tools.list({
  project:   "coding",
  tags:      ["destructive"],
  search:    "issue",          // matches name + description
  enabled:   true,
  callsLast: "7d",             // sorts by recent activity
});

// Single tool, fully hydrated (incl. permissions, audit summary)
const t = await mk.tools.get("github.create_issue");`}</CodeBlock>

    <h2>Tags</h2>
    <p>
      Tags are arbitrary strings used by permission rules + the dashboard's discovery UI.
      Conventions we recommend:
    </p>
    <ul>
      <li>
        <code>readonly</code> / <code>writeonly</code> / <code>destructive</code> — drives "danger"
        badges in the UI.
      </li>
      <li>
        <code>auth-required</code> / <code>public</code> — surfaces in OAuth audits.
      </li>
      <li>
        <code>provider:gmail</code> / <code>provider:github</code> — auto-set for connector tools;
        surfaces in filters.
      </li>
      <li>
        <code>domain:billing</code> / <code>domain:engineering</code> — your taxonomy; useful for
        big projects.
      </li>
    </ul>

    <h2>Enable / disable</h2>
    <p>
      Tools can be disabled without removing them — useful for "kill the send-email tool while we're
      testing" without losing the schema + audit history:
    </p>
    <CodeBlock lang="ts">{`await mk.tools.disable("gmail.send", { reason: "load testing in prod" });
// ... later ...
await mk.tools.enable("gmail.send");`}</CodeBlock>

    <h2>Bulk operations</h2>
    <CodeBlock lang="ts">{`// Disable every destructive tool
await mk.tools.bulk({
  project: "coding",
  filter:  { tags: ["destructive"] },
  action:  { kind: "disable", reason: "freeze before launch" },
});

// Add a tag to every Gmail tool
await mk.tools.bulk({
  project: "personal-assistant",
  filter:  { provider: "gmail" },
  action:  { kind: "tag", add: ["domain:inbox"] },
});`}</CodeBlock>

    <h2>Searching audit logs by tool</h2>
    <CodeBlock lang="ts">{`const events = await mk.audit.list({
  tool:   "gmail.send",
  since:  "2026-05-01",
  limit:  50,
});
for (const e of events) {
  console.log(e.calledAt, e.caller.id, e.result.status, e.latencyMs);
}`}</CodeBlock>

    <h2>Programmatic export</h2>
    <p>
      Get the registry in whatever shape you want — see{" "}
      <Link to="/docs/tools/export">Multi-protocol export</Link>.
    </p>
  </>
);

const ToolsExportBody = (
  <>
    <p>
      One tool definition; six output shapes. Pick the one your model or framework expects and the
      registry handles every schema conversion (JSON Schema dialects, parameter naming, streaming
      conventions, error envelopes).
    </p>

    <h2>Supported formats</h2>
    <table>
      <thead>
        <tr>
          <th>Format</th>
          <th>Use with</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>mcp</code>
          </td>
          <td>Claude Desktop, Cursor, Open WebUI, any MCP client</td>
          <td>HTTP-streamable; default</td>
        </tr>
        <tr>
          <td>
            <code>openai</code>
          </td>
          <td>OpenAI Chat Completions + Assistants</td>
          <td>JSON Schema draft 7</td>
        </tr>
        <tr>
          <td>
            <code>anthropic</code>
          </td>
          <td>Claude Messages API</td>
          <td>
            <code>input_schema</code> shape
          </td>
        </tr>
        <tr>
          <td>
            <code>langchain</code>
          </td>
          <td>LangChain TS or Python</td>
          <td>Returns importable Tool objects</td>
        </tr>
        <tr>
          <td>
            <code>mastra</code>
          </td>
          <td>Mastra Agent framework</td>
          <td>Mastra-native tool shape</td>
        </tr>
        <tr>
          <td>
            <code>rest</code>
          </td>
          <td>
            Anywhere you can <code>curl</code>
          </td>
          <td>Typed HTTP endpoints + OpenAPI doc</td>
        </tr>
      </tbody>
    </table>

    <h2>OpenAI function calling</h2>
    <CodeBlock lang="ts">{`const tools = await mk.tools.export({
  project: "personal-assistant",
  format:  "openai",
});

const reply = await openai.chat.completions.create({
  model:   "gpt-4o",
  tools,
  messages,
});`}</CodeBlock>

    <h2>Anthropic Tools</h2>
    <CodeBlock lang="ts">{`const tools = await mk.tools.export({
  project: "personal-assistant",
  format:  "anthropic",
});

await anthropic.messages.create({
  model:   "claude-sonnet-4-6",
  tools,
  messages,
});`}</CodeBlock>

    <h2>LangChain</h2>
    <CodeBlock lang="python">{`from langchain_openai import ChatOpenAI
from memkernel.langchain import load_tools

tools = load_tools(project="personal-assistant", api_key=os.environ["MK_API_KEY"])
agent = ChatOpenAI(model="gpt-4o").bind_tools(tools)

print(agent.invoke("Search my drive for Q4 plan."))`}</CodeBlock>

    <h2>Filtering before export</h2>
    <p>
      You usually want a subset — e.g. read-only tools for one agent and the full set for another.
      Pass a filter:
    </p>
    <CodeBlock lang="ts">{`const safe = await mk.tools.export({
  project: "personal-assistant",
  format:  "anthropic",
  filter:  { tags: ["readonly"], deny: ["gmail.send"] },
});`}</CodeBlock>

    <h2>Streaming + tool calls during generation</h2>
    <p>
      For models that stream tool calls (Anthropic Claude 4, OpenAI o3), MemKernel's exported tools
      support streaming results back as they arrive. The SDK takes care of bridging your model's
      protocol to MemKernel's:
    </p>
    <CodeBlock lang="ts">{`const stream = await mk.agents.runStream({
  project:  "personal-assistant",
  model:    "anthropic:claude-sonnet-4-6",
  messages,
});

for await (const event of stream) {
  switch (event.type) {
    case "text":         process.stdout.write(event.delta); break;
    case "tool_use":     console.log("calling", event.name); break;
    case "tool_result":  console.log("→", event.preview); break;
  }
}`}</CodeBlock>

    <h2>Versioning + drift</h2>
    <p>
      If you change a tool's input schema and a running agent has the old version cached, MemKernel
      surfaces a <code>tool_schema_drift</code> warning header so the SDK knows to re-export. The
      exports are versioned by tool revision — pin to a revision in production:
    </p>
    <CodeBlock lang="ts">{`const tools = await mk.tools.export({
  project: "personal-assistant",
  format:  "anthropic",
  pin:     "rev:42",   // ignore later schema bumps until you upgrade
});`}</CodeBlock>
  </>
);

const ToolsPermissionsBody = (
  <>
    <p>
      Permission rules attach to the tool they govern. Each rule grants (or denies) a subject (a
      client identity or agent) an action on the tool. Default is <code>deny</code>; nothing's
      callable until an explicit <code>allow</code> exists.
    </p>

    <h2>Set rules on a tool</h2>
    <CodeBlock lang="ts">{`await mk.tools.permissions.set({
  tool: "gmail.send",
  rules: [
    // Cursor can't send email
    { subject: "client:cursor", action: "call", effect: "deny" },
    // Claude can send, but only to the user themselves
    { subject: "client:claude", action: "call",
      scope: { to: { "$endsWith": "@me" } },
      effect: "allow" },
    // Trusted server agent can send freely
    { subject: "agent:weekly-report", action: "call", effect: "allow" },
  ],
});`}</CodeBlock>

    <h2>Common patterns</h2>

    <h3>Read-only Claude</h3>
    <CodeBlock lang="ts">{`await mk.permissions.bulk({
  project: "personal-assistant",
  rules: [
    { subject: "client:claude", target: "tool:*",
      action: "call", effect: "allow" },
    { subject: "client:claude", target: "tool:*",
      action: "call",
      scope:  { __tag: "destructive" },
      effect: "deny" },
  ],
});`}</CodeBlock>

    <h3>Tool sandbox for an untrusted client</h3>
    <CodeBlock lang="ts">{`await mk.permissions.bulk({
  project: "support",
  rules: [
    { subject: "client:demo-bot",
      target:  "tool:zendesk.search_tickets",
      action:  "call", effect: "allow" },
    { subject: "client:demo-bot",
      target:  "tool:*",
      action:  "call", effect: "deny" },
  ],
});`}</CodeBlock>

    <h3>Email allowlist by domain</h3>
    <CodeBlock lang="ts">{`{ subject: "client:claude", target: "tool:gmail.send", action: "call",
  scope:  { to: { "$endsWith": "@acme.com" } },
  effect: "allow" }`}</CodeBlock>

    <h2>Per-call evaluation flow</h2>
    <ol>
      <li>Caller is identified (MCP client id, API key, agent id).</li>
      <li>
        Input is validated against the tool's <code>inputSchema</code>.
      </li>
      <li>
        All matching rules are gathered. Specificity is computed; ties favour <code>deny</code>.
      </li>
      <li>Allowed scope predicates are evaluated against the input arguments.</li>
      <li>
        The decision (+ which rules matched + which scope predicates failed) lands in the audit log
        even on success.
      </li>
    </ol>

    <p>
      Full rule grammar is documented in <Link to="/docs/concepts/permissions">Permissions</Link>.
    </p>
  </>
);

const ToolsVersioningBody = (
  <>
    <p>
      Tools change. Schemas grow optional fields, descriptions get rewritten, edge cases get
      patched. MemKernel preserves every revision so every audit log entry is replayable against the
      schema it was actually called with — even if you've since bumped the tool three more times.
    </p>

    <h2>Bumping a revision</h2>
    <CodeBlock lang="ts">{`await mk.tools.update("gmail.search", {
  inputSchema: nextSchema,
  changelog:   "added 'in:label' filter parameter",
  breaking:    false,
});`}</CodeBlock>
    <p>
      Mark the change as <code>breaking: true</code> if removing fields or tightening types.
      Breaking bumps:
    </p>
    <ul>
      <li>Force re-export for every consumer.</li>
      <li>
        Surface a <code>tool_schema_drift</code> header to in-flight clients.
      </li>
      <li>
        Are <strong>blocked</strong> for tools that have been called in the last 7 days, unless you
        opt-in with <code>force: true</code>.
      </li>
    </ul>

    <h2>Pinning a revision in production</h2>
    <p>
      In production code, pin the tool revision you tested against so a surprise schema bump in the
      dashboard doesn't break the agent:
    </p>
    <CodeBlock lang="ts">{`const tools = await mk.tools.export({
  project: "personal-assistant",
  format:  "anthropic",
  pin:     "rev:42",   // ignore later changes
});`}</CodeBlock>
    <p>
      To upgrade, bump the pin after re-testing. The dashboard's tool detail page shows which
      revisions are currently in use across your deployed agents.
    </p>

    <h2>Diffing revisions</h2>
    <CodeBlock lang="ts">{`const diff = await mk.tools.diff("gmail.search", { from: 41, to: 42 });
// → { added: ["in_label"], removed: [], changedDescriptions: [...] }`}</CodeBlock>

    <h2>Replaying a historical call</h2>
    <p>Useful when you want to reproduce an old failure with the schema it actually ran under:</p>
    <CodeBlock lang="ts">{`const replay = await mk.audit.replay({
  eventId: "evt_01H...",
  dry:     true,   // don't actually call upstream
});
console.log(replay.toolRevision, replay.normalizedArgs, replay.wouldRetry);`}</CodeBlock>

    <h2>Deprecation lifecycle</h2>
    <CodeBlock lang="ts">{`await mk.tools.deprecate("gmail.legacy_send", {
  since: "2026-06-01",
  replacement: "gmail.send",
  hardRemoveAt: "2026-09-01",
});`}</CodeBlock>
    <p>
      Deprecated tools are still callable but the SDK + MCP exports return a{" "}
      <code>X-MK-Deprecation</code> header and an annotated description so the model knows to prefer
      the replacement.
    </p>
  </>
);

const MemoryStoresBody = (
  <>
    <p>
      Memory stores are typed key-value (and optionally document) collections backed by Postgres,
      with an optional vector projection for semantic recall. Every store is project-scoped by
      default; use the user / shared scopes for cross-project state.
    </p>

    <h2>Create a store</h2>
    <CodeBlock lang="ts">{`await mk.memory.createStore({
  project: "personal-assistant",
  name:    "preferences",
  scope:   "project",          // user | project | session | shared
  schema:  {                   // optional JSON Schema — recommended
    type: "object",
    properties: {
      key:   { type: "string", maxLength: 200 },
      value: { type: "string" },
    },
    additionalProperties: false,
  },
  semantic: true,
  embeddingModel: "openai/text-embedding-3-small",
  ttl:      "30d",             // optional auto-expire
});`}</CodeBlock>

    <h2>Read / write</h2>
    <CodeBlock lang="ts">{`// Single key
await mk.memory.set("preferences", "tone", "concise and direct");
const tone = await mk.memory.get("preferences", "tone");

// Bulk upsert
await mk.memory.upsert("preferences", [
  { key: "tz",        value: "Europe/Berlin" },
  { key: "formality", value: "first-name" },
]);

// List with filter
const all = await mk.memory.list({
  store: "preferences",
  filter: { key: { "$startsWith": "tone." } },
  limit:  50,
});`}</CodeBlock>

    <h2>Recall semantically</h2>
    <p>
      Only stores with <code>semantic: true</code> support recall. The query is embedded with the
      store's configured model; results are ranked by cosine similarity:
    </p>
    <CodeBlock lang="ts">{`const hits = await mk.memory.recall({
  store:  "active-threads",
  query:  "what threads are open about Q4 planning?",
  k:      5,
  filter: { status: "open" },
});
for (const h of hits) console.log(h.score, h.key, h.value);`}</CodeBlock>

    <h2>Auto-injection into prompts</h2>
    <p>
      For projects whose MCP URL is wired into a model client, MemKernel can automatically inject
      the top-k memory hits as system messages on every turn — toggled per store with{" "}
      <code>injectInto</code>:
    </p>
    <CodeBlock lang="ts">{`await mk.memory.updateStore("preferences", {
  injectInto: { mcpUrl: true, sdkAutoFetch: true, k: 3 },
});`}</CodeBlock>

    <h2>Forgetting + privacy</h2>
    <ul>
      <li>
        <code>mk.memory.delete(store, key)</code> — hard delete.
      </li>
      <li>
        <code>mk.memory.forget(store, query)</code> — semantic forget: deletes everything matching
        the query above a similarity threshold.
      </li>
      <li>
        <code>mk.memory.exportAll(store)</code> — for user data exports.
      </li>
      <li>TTL'd entries are purged from disk within 24h of expiry, not just hidden.</li>
    </ul>

    <p>
      See <Link to="/docs/memory/vector">Vector & RAG</Link> for how semantic recall is backed.
    </p>
  </>
);

const MemoryVectorBody = (
  <>
    <p>
      The vector layer is what powers semantic recall on memory stores and document indexes. Set{" "}
      <code>semantic: true</code> on a store (or create a standalone <code>vectors</code> index) and
      MemKernel handles embedding, indexing, similarity search, and metadata filtering for you.
    </p>

    <h2>From tool handlers</h2>
    <CodeBlock lang="ts">{`async function handler(input, ctx) {
  const hits = await ctx.memory.recall({
    store: "research-docs",
    query: input.question,
    k:     5,
  });
  return summarize(hits);
}`}</CodeBlock>

    <h2>From SDK or REST</h2>
    <CodeBlock lang="ts">{`const hits = await mk.vectors.search({
  index:  "research-papers",
  query:  "how does memkernel route mcp calls",
  k:      8,
  filter: { year: { "$gte": 2025 } },
});`}</CodeBlock>

    <h2>Full RAG pipeline in one call</h2>
    <p>Skip the hand-rolled retrieve → prompt → generate loop:</p>
    <CodeBlock lang="ts">{`const out = await mk.vectors.rag({
  index:  "research-papers",
  query:  "summarize MemKernel routing",
  model:  "anthropic:claude-sonnet-4-6",
  rerank: { model: "voyage/rerank-2", topK: 5 },
});
// → { answer, citations }`}</CodeBlock>

    <h2>Reranking</h2>
    <p>
      Vector recall returns the top-k by cosine; rerankers re-score the top-k by an LLM-trained
      relevance head. Adding a reranker on top of a coarse retrieval often improves answer quality
      by 10–20%. MemKernel ships with the following reranker integrations:
    </p>
    <ul>
      <li>
        <code>voyage/rerank-2</code> (recommended for general)
      </li>
      <li>
        <code>cohere/rerank-3</code>
      </li>
      <li>
        <code>jina/reranker-v2-base-multilingual</code>
      </li>
    </ul>

    <h2>Switching embedding models</h2>
    <p>
      Switching is online: trigger a re-embed and the old vectors keep serving until the new ones
      land:
    </p>
    <CodeBlock lang="ts">{`await mk.vectors.reembed("research-papers", {
  to: "voyage/voyage-3",
  // optional: backfill historical entries in batches of 1k
  batchSize: 1000,
});`}</CodeBlock>

    <p>
      See <Link to="/docs/concepts/vector">Vector concept</Link> for backend picks.
    </p>
  </>
);

const KnowledgeBaseBody = (
  <>
    <p>
      A knowledge base is a higher-level wrapper around a corpus of documents. Point it at one or
      more sources — a Drive folder, a Notion database, an S3 bucket, a sitemap — and MemKernel
      automatically: fetches, chunks, embeds, indexes, syncs on a schedule, and exposes a single
      search interface to agents.
    </p>

    <h2>Create a knowledge base</h2>
    <CodeBlock lang="ts">{`await mk.knowledge.create({
  project: "research",
  name:    "papers",
  sources: [
    { kind: "drive-folder",     credential: "google",   folderId: "0AB..." },
    { kind: "notion-database",  credential: "notion",   databaseId: "..." },
    { kind: "website",          url: "https://docs.acme.com", sitemap: true },
  ],
  chunking: { mode: "semantic", maxTokens: 600 },
  embedding: { model: "openai/text-embedding-3-small" },
  syncInterval: "1h",
});`}</CodeBlock>

    <h2>Source types</h2>
    <table>
      <thead>
        <tr>
          <th>Source</th>
          <th>Sync mode</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Drive folder</td>
          <td>
            Push (Drive webhook) + pull every <code>syncInterval</code>
          </td>
          <td>Inherits ACLs</td>
        </tr>
        <tr>
          <td>Notion database</td>
          <td>Pull on interval</td>
          <td>Schema-aware; respects page permissions</td>
        </tr>
        <tr>
          <td>Confluence space</td>
          <td>Pull on interval</td>
          <td>Preserves heading anchors</td>
        </tr>
        <tr>
          <td>Website (sitemap)</td>
          <td>Pull on interval</td>
          <td>Headless render + readability extraction</td>
        </tr>
        <tr>
          <td>S3 / R2 / GCS bucket</td>
          <td>Push (event) + pull</td>
          <td>Prefix-scoped</td>
        </tr>
        <tr>
          <td>Postgres query</td>
          <td>Pull on interval</td>
          <td>Re-runs query; diffs against snapshot</td>
        </tr>
        <tr>
          <td>Local files (self-host)</td>
          <td>Watch via inotify</td>
          <td>For on-prem only</td>
        </tr>
      </tbody>
    </table>

    <h2>Querying</h2>
    <CodeBlock lang="ts">{`const hits = await mk.knowledge.search({
  knowledge: "papers",
  query:     "comparison of pgvector vs qdrant for 100k docs",
  k:         5,
  filter:    { source: "notion-database", section: { "$ne": "Drafts" } },
});`}</CodeBlock>
    <p>
      Each hit carries: chunk text, score, source URL (clickable in the dashboard), provenance
      metadata (which page/file/notion-page, which section), and the version of the source document
      that was indexed.
    </p>

    <h2>Permissions inheritance</h2>
    <p>
      For sources that have their own access model (Drive, Notion), MemKernel preserves the original
      ACLs at query time. A search by a client only returns chunks the underlying source would have
      allowed that user to see.
    </p>

    <h2>Sync state + lag</h2>
    <CodeBlock lang="ts">{`const info = await mk.knowledge.status("papers");
// → { lastSync: "2026-05-29T01:02:00Z",
//     pending: 24, indexed: 4892, lagSeconds: 173 }`}</CodeBlock>
  </>
);

const IngestSourcesBody = (
  <>
    <p>
      Sources are the leaves of a knowledge base. Each one declares where data comes from, how often
      to sync, which fields to index, and how to chunk. You can mix multiple sources into one
      knowledge base — agents see a unified search across all of them.
    </p>

    <h2>Add a source</h2>
    <CodeBlock lang="ts">{`await mk.knowledge.addSource("research-docs", {
  kind:         "drive-folder",
  credential:   "google",
  folderId:     "0AB...",
  syncInterval: "1h",
  chunking:     { mode: "semantic", maxTokens: 600, overlap: 64 },
  fileTypes:    ["pdf", "docx", "md", "txt"],
  // Optional: restrict which subpaths to ingest
  pathPrefix:   "Research/",
});`}</CodeBlock>

    <h2>Webhook-driven sources</h2>
    <p>
      Drive, Notion, GitHub, and S3 emit change events; we subscribe so new/changed documents land
      in the index within ~30s, not on the next interval.
    </p>

    <h2>Custom-script sources (self-host)</h2>
    <CodeBlock lang="ts">{`await mk.knowledge.addSource("research-docs", {
  kind: "custom",
  // Runs every 'syncInterval' inside MemKernel's worker container.
  // Returns documents to index; MemKernel diffs against last snapshot.
  handler: async (ctx) => {
    const rows = await ctx.providers.postgres.query(\`
      SELECT id, title, body, updated_at
      FROM research_notes
      WHERE updated_at > $1
    \`, [ctx.checkpoint]);
    return rows.map(r => ({
      id:       String(r.id),
      title:    r.title,
      content:  r.body,
      updatedAt: r.updated_at,
    }));
  },
  syncInterval: "5m",
});`}</CodeBlock>

    <h2>Chunking strategies</h2>
    <p>Three modes, configurable per source:</p>
    <ul>
      <li>
        <code>fixed</code> — every N tokens (default 512, overlap 64). Simple; bad for
        paragraph-shaped content.
      </li>
      <li>
        <code>semantic</code> — split on sentence/paragraph boundaries. Recommended for prose.
      </li>
      <li>
        <code>recursive</code> — try heading → section → paragraph → sentence. Recommended for code
        + Markdown.
      </li>
    </ul>

    <h2>Pause + resume</h2>
    <CodeBlock lang="ts">{`await mk.knowledge.pauseSource("research-docs", "drive-folder-0AB");
// ... maintenance ...
await mk.knowledge.resumeSource("research-docs", "drive-folder-0AB");`}</CodeBlock>
  </>
);

const AuthEmailBody = (
  <>
    <p>
      Email + password is the simplest path to a MemKernel account. Passwords are stored as Argon2id
      hashes (memCost 19 MiB, t=2, p=1). Sessions are opaque bearer tokens with a 30-day default
      TTL.
    </p>

    <h2>Sign up</h2>
    <CodeBlock lang="bash">{`curl -X POST https://api.memkernel.com/v1/auth/sign-up \\
  -H "Content-Type: application/json" \\
  -d '{"email":"you@example.com","password":"a-very-strong-passphrase"}'
# → 201 { "userId": "u_01H...", "verificationRequired": true }`}</CodeBlock>
    <p>
      <strong>Password rules:</strong> minimum 12 characters; checked against the haveibeenpwned
      k-anonymity API at sign-up; rejected if it's been seen in any breach.
    </p>

    <h2>Verify email</h2>
    <p>
      We send a verification link to the supplied email. The link is single-use, 24h TTL. Until
      verified, you can sign in but you can't create credentials.
    </p>

    <h2>Sign in</h2>
    <CodeBlock lang="bash">{`curl -X POST https://api.memkernel.com/v1/auth/sign-in \\
  -H "Content-Type: application/json" \\
  -d '{"email":"you@example.com","password":"..."}'
# → 200 {
#     "token":     "mks_session_a1b2c3...",
#     "expiresAt": "2026-06-28T01:02:00Z",
#     "user":      { "id": "u_01H...", "email": "you@example.com" }
#   }`}</CodeBlock>
    <p>The session token is opaque and stored hashed server-side. Treat it like a password.</p>

    <h2>Two-factor (recommended)</h2>
    <p>
      Enable TOTP from Settings → Security. Once enabled, sign-in becomes a two-step exchange:{" "}
      <code>/v1/auth/sign-in</code> returns a short-lived <code>mfa_pending</code> token + the
      second step <code>/v1/auth/mfa/verify</code> upgrades it to a real session.
    </p>

    <h2>Password reset</h2>
    <CodeBlock lang="bash">{`curl -X POST https://api.memkernel.com/v1/auth/password/reset-request \\
  -d '{"email":"you@example.com"}'
# → always returns 202 (no user enumeration)`}</CodeBlock>
    <p>
      The reset link is single-use, 1h TTL. Completing a reset invalidates every existing session.
    </p>

    <h2>Rate limiting</h2>
    <ul>
      <li>Sign-in: 5 attempts / 15 min / IP, 10 / 15 min / email.</li>
      <li>Sign-up: 5 / hour / IP.</li>
      <li>Reset request: 5 / hour / email.</li>
    </ul>
  </>
);

const AuthOauthBody = (
  <>
    <p>
      OAuth-based sign-in (Google, GitHub) is the recommended onboarding path. It uses the same
      OAuth handshake as a regular tool credential, with one twist: the resulting credential is
      bound to your MemKernel user and pre-populated for any project you create.
    </p>

    <h2>Flow</h2>
    <ol>
      <li>
        Browser hits <code>POST /v1/auth/oauth/google/start</code> → we return a{" "}
        <code>redirectUrl</code> + a one-time <code>state</code>.
      </li>
      <li>You redirect the user to that URL; they consent at Google.</li>
      <li>
        Google calls back to <code>/v1/auth/oauth/google/callback</code> with code + state. We
        verify state, exchange the code, fetch the user's profile + email.
      </li>
      <li>
        If a MemKernel user exists for that email, we sign in. If not, we create one. Either way the
        OAuth credential is stored encrypted under the user's envelope key.
      </li>
      <li>
        We mint a session token, set the secure HTTP-only cookie, and redirect to{" "}
        <code>/onboarding</code>.
      </li>
    </ol>

    <h2>Provider scopes (sign-in only)</h2>
    <p>For account sign-in we request the minimum identity scopes only:</p>
    <CodeBlock lang="json">{`// google
["openid", "email", "profile"]

// github
["read:user", "user:email"]`}</CodeBlock>
    <p>
      Tool scopes (Drive, Gmail, Repo) are requested separately when you connect a connector. We
      never silently widen scopes.
    </p>

    <h2>Linking multiple OAuth identities</h2>
    <p>
      You can attach more than one OAuth identity to the same account (e.g. sign in with Google{" "}
      <em>or</em> GitHub). From Settings → Account → Identities, click{" "}
      <strong>Link provider</strong>.
    </p>

    <h2>Errors</h2>
    <table>
      <thead>
        <tr>
          <th>Code</th>
          <th>Meaning</th>
          <th>Fix</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>oauth_state_mismatch</code>
          </td>
          <td>State token didn't match (possible CSRF)</td>
          <td>Restart the flow</td>
        </tr>
        <tr>
          <td>
            <code>oauth_email_unverified</code>
          </td>
          <td>Provider didn't return a verified email</td>
          <td>Verify your email on the provider then retry</td>
        </tr>
        <tr>
          <td>
            <code>oauth_account_taken</code>
          </td>
          <td>Another MemKernel user already linked this identity</td>
          <td>Sign in as the existing account</td>
        </tr>
        <tr>
          <td>
            <code>oauth_provider_error</code>
          </td>
          <td>Provider returned an error during exchange</td>
          <td>Check provider status + retry</td>
        </tr>
      </tbody>
    </table>
  </>
);

const AuthApiKeysBody = (
  <>
    <p>
      API keys are how server-to-server callers (your scripts, deployments, CI jobs) authenticate to
      MemKernel. Two flavours:
    </p>
    <ul>
      <li>
        <code>mks_live_…</code> — Project key. Scoped to one project; can't touch others. Use for
        production deploys (one per service per project).
      </li>
      <li>
        <code>mks_user_…</code> — User key. Can act across every project you own. Useful for one-off
        scripts; don't embed in deploys.
      </li>
    </ul>

    <h2>Create a project key</h2>
    <CodeBlock lang="bash">{`curl -X POST https://api.memkernel.com/v1/api-keys \\
  -H "Authorization: Bearer <session-token>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name":  "prod-coding",
    "scope": { "project": "coding" },
    "permissions": ["tools.export", "tools.call", "memory.recall"]
  }'
# → 201 { "id": "ak_01H...", "token": "mks_live_..." }     # shown ONCE`}</CodeBlock>

    <h2>Listing + rotating</h2>
    <CodeBlock lang="ts">{`// List active keys (token never exposed again)
const keys = await mk.apiKeys.list();

// Rotate — old key valid for 5 more minutes
const { newToken } = await mk.apiKeys.rotate("ak_01H...");

// Revoke immediately
await mk.apiKeys.revoke("ak_01H...");`}</CodeBlock>

    <h2>Scoping</h2>
    <p>
      You can further restrict what a project key can do via the
      <code>permissions</code> field:
    </p>
    <CodeBlock lang="json">{`{
  "permissions": [
    "tools.list", "tools.export", "tools.call",
    "memory.recall"
  ]
}`}</CodeBlock>
    <p>
      Anything not listed returns <code>403 insufficient_scope</code>. Full permission catalogue is
      in the dashboard's "create key" form.
    </p>

    <h2>Token rotation in production</h2>
    <p>
      Best practice: store both <code>MK_API_KEY</code> and <code>MK_API_KEY_NEXT</code> in your
      secret manager; on startup the SDK prefers <code>NEXT</code>, but falls back if it's missing.
      Rotation then becomes: deploy with new <code>NEXT</code> → swap values → drop the old. No
      downtime.
    </p>

    <h2>Secret-scanning</h2>
    <p>
      We participate in GitHub's secret-scanning programme. If a <code>mks_live_…</code> token ever
      lands in a public repo we revoke it within minutes and email the account owner. You should
      still rotate the key on your end.
    </p>
  </>
);

const AuthSessionsBody = (
  <>
    <p>
      Sessions are opaque bearer tokens that identify a human user across the dashboard + SDK.
      They're distinct from API keys (which are for service-to-service) and from MCP tokens (which
      authorize an MCP client to a single project URL).
    </p>

    <h2>Anatomy</h2>
    <CodeBlock lang="ts">{`Session {
  id:        string;            // sess_01H...
  user:      string;            // u_01H...
  token:     string;            // mks_session_... (hashed at rest)
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string;
  client: {
    userAgent: string;
    ip:        string;          // truncated to /24 (v4) or /48 (v6)
    location:  string;          // city, best-effort
  };
}`}</CodeBlock>

    <h2>Auth header</h2>
    <CodeBlock lang="bash">{`curl https://api.memkernel.com/v1/me \\
  -H "Authorization: Bearer mks_session_..."`}</CodeBlock>

    <h2>TTL + sliding window</h2>
    <p>
      Default TTL is 30 days. Each successful use extends the expiry by another 30 days (sliding
      session). Hard maximum is 90 days from first issue regardless of activity; after that the user
      must re-authenticate.
    </p>
    <p>You can lower the TTL per account from Settings → Security → Session lifetime.</p>

    <h2>Listing + revoking</h2>
    <CodeBlock lang="ts">{`// All active sessions
const sessions = await mk.sessions.list();

// Revoke one (forces logout on that device)
await mk.sessions.revoke("sess_01H...");

// Revoke all except the current one ("Sign out everywhere else")
await mk.sessions.revokeOthers();`}</CodeBlock>

    <h2>Compromise drill</h2>
    <p>If you suspect a token leak:</p>
    <ol>
      <li>
        Hit Settings → Security → <strong>Revoke everything</strong> (kills all sessions, all API
        keys, all MCP tokens).
      </li>
      <li>Change your password.</li>
      <li>Rotate any tool credentials that script the affected account.</li>
      <li>Check the audit log for actions in the suspicious window.</li>
    </ol>
  </>
);

const AgentsOverviewBody = (
  <>
    <p>
      Today MemKernel is everything <em>around</em> the model — tools, memory, vectors, identity,
      permissions. The agent runtime is the next phase: hosted execution of the model loop itself,
      using the project's wired stack. Schedule it, scale it, audit it, roll it back. No more "we
      built it, where do we deploy it."
      <Soon />
    </p>

    <h2>What's shipping</h2>
    <table>
      <thead>
        <tr>
          <th>Capability</th>
          <th>Status</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Agent registry (define + version)</td>
          <td>Q3 2026</td>
          <td>YAML/TS-defined, semver tags</td>
        </tr>
        <tr>
          <td>Long-running runtime + checkpointing</td>
          <td>Q3 2026</td>
          <td>Resumes within 5s of node failure</td>
        </tr>
        <tr>
          <td>Cron scheduling</td>
          <td>Q3 2026</td>
          <td>Per-project timezone</td>
        </tr>
        <tr>
          <td>Skill imports (skills.sh)</td>
          <td>Q3 2026</td>
          <td>First-class agent shape</td>
        </tr>
        <tr>
          <td>Multi-agent workflows + handoffs</td>
          <td>Q4 2026</td>
          <td>Typed message passing</td>
        </tr>
        <tr>
          <td>One-click deploy</td>
          <td>Q4 2026</td>
          <td>Cloud + Docker + K8s targets</td>
        </tr>
        <tr>
          <td>Marketplace / sharing</td>
          <td>2027</td>
          <td>Public + private</td>
        </tr>
      </tbody>
    </table>

    <h2>The design we're committing to</h2>
    <ul>
      <li>
        <strong>Agents are explicit definitions, not chat sessions.</strong> A definition declares:
        model, tools (by tag or list), memory scopes, schedule, retry policy, max cost.
      </li>
      <li>
        <strong>One agent = one Postgres row.</strong> Audit, edit, roll back. Versioned forever.
      </li>
      <li>
        <strong>State is checkpointed.</strong> If the agent is mid-tool loop when a node dies, it
        resumes from the last checkpoint, not from scratch.
      </li>
      <li>
        <strong>Budgets are enforced.</strong> Max tokens, max tool calls, max wallclock, max dollar
        spend — pick any combination.
      </li>
      <li>
        <strong>Skills are first-class.</strong> Drop a <code>foo.md</code> in your repo following
        the skills.sh convention and it becomes a callable agent.
      </li>
    </ul>

    <h2>Why we waited</h2>
    <p>
      We didn't want to ship a runtime that relied on a half-built tool registry. The order matters:
      tools, memory, vectors, identity first — agents on top.
    </p>

    <p>
      Follow the <Link to="/docs/changelog">changelog</Link> for ship dates.
    </p>
  </>
);

const SkillsBody = (
  <>
    <p>
      Skills are reusable agent capabilities packaged in the open <code>skills.sh</code> format.
      MemKernel's agent runtime imports them as first-class entities — they sit alongside tools and
      MCP servers, are versioned, and can be shared across projects.
      <Soon />
    </p>

    <h2>Format</h2>
    <p>A skill is one Markdown file with YAML frontmatter:</p>
    <CodeBlock lang="md">{`---
name:        refactor-review
description: Reviews a PR for refactor smells.
model:       anthropic:claude-sonnet-4-6
inputs:
  pr_url:    { type: string, format: uri }
  language:  { type: string, enum: [ts, py, go, rust] }
tools:       [github.read_pr, github.read_file]
memory:      [code-review-history]
budget:
  maxTokens: 30000
  maxCost:   "$0.50"
---

# Refactor review skill

When called, fetch the PR diff via \`github.read_pr\`. For each touched
file, read the file body via \`github.read_file\` and look for:

- functions over 50 lines,
- nesting depth > 4,
- duplication across files,
- repeated string literals that should be constants.

Return a structured report. Keep prose short; the developer will skim it.`}</CodeBlock>

    <h2>Import a skill</h2>
    <CodeBlock lang="ts">{`// From a GitHub repo (any subpath; we read the .md + sibling assets)
await mk.skills.import({
  project: "coding",
  source:  "github:my-org/skills/refactor-review@v1.2.0",
});

// From a local file (self-host or CLI)
await mk.skills.import({
  project: "coding",
  source:  "file:./skills/refactor-review.md",
});

// From a URL
await mk.skills.import({
  project: "coding",
  source:  "https://skills.example.com/refactor-review.md",
});`}</CodeBlock>

    <h2>Call a skill</h2>
    <CodeBlock lang="ts">{`const report = await mk.skills.run({
  project: "coding",
  skill:   "refactor-review",
  input:   { pr_url: "https://github.com/...", language: "ts" },
});`}</CodeBlock>

    <h2>Sharing</h2>
    <p>
      Skills can be marked public; a marketplace is on the roadmap. Until then, distribute via any
      git host. We'll lint the frontmatter against the schema on import and warn on missing fields.
    </p>
  </>
);

const RuntimeBody = (
  <>
    <p>
      The agent runtime executes agents inside MemKernel. Stateful, long-running, event-driven, and
      rollback-safe. Backed by Postgres for state and optional Redis for in-flight job queues.
      <Soon />
    </p>

    <h2>Execution model</h2>
    <ul>
      <li>
        Each agent run is a tree of <strong>steps</strong>: tool calls, sub-skills, LLM turns. Every
        step is a Postgres row.
      </li>
      <li>
        On node failure, the supervisor reaps the run and resumes from the last{" "}
        <code>committed</code> step within ~5s.
      </li>
      <li>
        Long runs (hours) are supported. We don't keep the LLM context in RAM — it's reconstructed
        from steps on resume.
      </li>
      <li>
        Cancellation is cooperative: <code>mk.agents.cancel(runId)</code> flips a flag the
        supervisor checks between steps.
      </li>
    </ul>

    <h2>Budgets + safety</h2>
    <CodeBlock lang="yaml">{`# Per-agent budget — runtime kills the run if any of these is hit
budget:
  maxTokens:    50000     # combined input + output
  maxToolCalls: 25
  maxWallclock: 600s
  maxCost:      "$1.00"
  fallbackOnBudget: "warn"  # or "fail"`}</CodeBlock>

    <h2>Determinism mode</h2>
    <p>
      For agents you want to replay byte-identical (testing, audits), enable determinism mode. The
      runtime records every model output hash + tool result; replaying validates each step against
      the snapshot. Useful for "the agent did X; what was the LLM seeing when it decided that?".
    </p>

    <h2>Resumption from human input</h2>
    <p>
      Pause an agent mid-run waiting for a human to answer a question. We surface a callback URL (or
      webhook + email) and the agent resumes when the human responds via the dashboard or the API.
    </p>
  </>
);

const SchedulingBody = (
  <>
    <p>
      Cron-style scheduling for any agent. Useful for report generation, monitoring, daily syncs,
      weekly briefings, and any other "do this thing on a clock" workflow.
      <Soon />
    </p>

    <h2>Schedule an agent</h2>
    <CodeBlock lang="ts">{`await mk.agents.schedule({
  agent:    "weekly-briefing",
  cron:     "0 9 * * MON",     // every Monday 09:00
  timezone: "Europe/Berlin",
  input:    { sinceLastRun: true },
  overlap:  "skip",            // skip | queue | replace
});`}</CodeBlock>

    <h2>Cron grammar</h2>
    <p>
      Standard 5-field crontab + the extensions <code>@yearly</code>, <code>@monthly</code>,{" "}
      <code>@weekly</code>, <code>@daily</code>, <code>@hourly</code>, <code>@every 5m</code>.
      Per-schedule timezone (IANA); DST-respecting.
    </p>

    <h2>Overlap policies</h2>
    <ul>
      <li>
        <code>skip</code> — if the previous run is still going, drop this firing.
      </li>
      <li>
        <code>queue</code> — queue this firing; run after the previous completes. Bounded to 5 deep;
        further firings are skipped.
      </li>
      <li>
        <code>replace</code> — cancel the previous run, start fresh.
      </li>
    </ul>

    <h2>Missed firings (long downtime)</h2>
    <p>
      By default we backfill at most one firing on recovery. Set <code>backfill: "all"</code> on
      agents that need to catch up (e.g. data sync), or <code>backfill: "none"</code> to never
      backfill.
    </p>

    <h2>Pause + unpause</h2>
    <CodeBlock lang="ts">{`await mk.agents.pauseSchedule("weekly-briefing");
// ...
await mk.agents.resumeSchedule("weekly-briefing");`}</CodeBlock>
  </>
);

const DeploymentsBody = (
  <>
    <p>
      Deploy agents with one click to cloud, Docker, Kubernetes, edge, or serverless targets.
      Built-in autoscaling, structured logs, metrics, and rollbacks.
      <Soon />
    </p>

    <h2>Targets</h2>
    <table>
      <thead>
        <tr>
          <th>Target</th>
          <th>Cold start</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>MemKernel Cloud</td>
          <td>~0 ms</td>
          <td>Default; pinned region</td>
        </tr>
        <tr>
          <td>Docker (anywhere)</td>
          <td>~2 s</td>
          <td>Single image we publish to GHCR</td>
        </tr>
        <tr>
          <td>Kubernetes (Helm)</td>
          <td>~3 s</td>
          <td>Chart + values templates</td>
        </tr>
        <tr>
          <td>AWS Lambda / Fargate</td>
          <td>varies</td>
          <td>Adapter container we publish</td>
        </tr>
        <tr>
          <td>Cloudflare Workers</td>
          <td>~50 ms</td>
          <td>For short-lived agents only</td>
        </tr>
      </tbody>
    </table>

    <h2>Deploy flow</h2>
    <ol>
      <li>
        <code>mk.deploy.create({"{ agent, target }"})</code> — we build a deploy plan + run
        pre-flight checks (budget, credential health, permission coverage).
      </li>
      <li>
        Plan is shown; you approve via dashboard or <code>mk.deploy.apply(planId)</code>.
      </li>
      <li>
        Rollout (default: blue-green) — traffic shifts to the new revision; old revision keeps
        serving until cutover.
      </li>
      <li>Auto-rollback if error rate jumps &gt; 2× baseline within the first 10 min.</li>
    </ol>

    <h2>Rollback</h2>
    <CodeBlock lang="ts">{`await mk.deploy.rollback({
  agent:      "weekly-briefing",
  toRevision: "rev:14",
});`}</CodeBlock>

    <h2>Observability</h2>
    <ul>
      <li>Structured logs streamed to your sink (Datadog, Loki, S3).</li>
      <li>Per-run cost + tool-call counts.</li>
      <li>Pre-built dashboards for tail latency + error rate by tool.</li>
      <li>Webhooks on deploy / failure / rollback.</li>
    </ul>
  </>
);

const RestApiBody = (
  <>
    <p>
      The REST API is the foundation under every SDK. Plain HTTP, JSON bodies, standard status
      codes. If you can <code>curl</code>, you can use MemKernel.
    </p>

    <h2>Base URL + versioning</h2>
    <CodeBlock>{`https://api.memkernel.com/v1`}</CodeBlock>
    <p>
      Major versions only ever break by going to <code>/v2</code>; we keep <code>/v1</code> alive
      for at least 12 months after the cut. Minor releases are additive — we never remove fields or
      change response shapes within a major version.
    </p>

    <h2>Authentication</h2>
    <CodeBlock>{`Authorization: Bearer <session-or-api-key-or-mcp-token>`}</CodeBlock>
    <p>
      Three token flavours, distinguished by prefix: <code>mks_session_…</code> (user sessions),{" "}
      <code>mks_live_…</code> + <code>mks_user_…</code> (API keys), <code>mks_mcp_…</code> (MCP-only
      project tokens).
    </p>

    <h2>Response envelope</h2>
    <CodeBlock lang="json">{`// Success
{
  "data":  { /* result */ },
  "error": null,
  "meta":  { "request_id": "req_abc", "took_ms": 17 }
}

// Failure
{
  "data":  null,
  "error": {
    "code":    "tool_input_invalid",
    "message": "Expected number; received string.",
    "field":   "days",
    "doc":     "https://docs.memkernel.com/docs/api/rest#tool_input_invalid"
  },
  "meta":  { "request_id": "req_abc" }
}`}</CodeBlock>

    <h2>Status codes</h2>
    <table>
      <thead>
        <tr>
          <th>Code</th>
          <th>Meaning</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>200 / 201 / 204</td>
          <td>Success</td>
          <td>POST returns 201 + Location</td>
        </tr>
        <tr>
          <td>400</td>
          <td>Validation</td>
          <td>Schema or input violation</td>
        </tr>
        <tr>
          <td>401</td>
          <td>Auth</td>
          <td>Missing / invalid bearer</td>
        </tr>
        <tr>
          <td>403</td>
          <td>Permissions</td>
          <td>Bearer is valid but not allowed</td>
        </tr>
        <tr>
          <td>404</td>
          <td>Not found</td>
          <td>—</td>
        </tr>
        <tr>
          <td>409</td>
          <td>Conflict</td>
          <td>Slug taken, optimistic-lock fail</td>
        </tr>
        <tr>
          <td>422</td>
          <td>Domain rule</td>
          <td>e.g. "can't delete a project with active runs"</td>
        </tr>
        <tr>
          <td>429</td>
          <td>Rate-limited</td>
          <td>
            Includes <code>Retry-After</code>
          </td>
        </tr>
        <tr>
          <td>5xx</td>
          <td>Our fault</td>
          <td>Safe to retry with backoff</td>
        </tr>
      </tbody>
    </table>

    <h2>Pagination</h2>
    <p>
      Cursor-based. List endpoints accept <code>limit</code> (max 100, default 25) and{" "}
      <code>cursor</code>; responses include <code>meta.next_cursor</code> when more rows exist.
    </p>
    <CodeBlock>{`GET /v1/projects/coding/audit?limit=50&cursor=eyJ0eXAiOi...`}</CodeBlock>

    <h2>Rate limits</h2>
    <p>Per-key, per-minute. Limits surface as response headers:</p>
    <CodeBlock>{`X-RateLimit-Limit:     1000
X-RateLimit-Remaining: 994
X-RateLimit-Reset:     1717075800
Retry-After:           7    (on 429 only)`}</CodeBlock>
    <p>
      Free: 10 tool calls / min. Pro: 10 000 / min. API calls that
      <em>aren't</em> tool calls (reads of registry, audit, etc.) have a higher cap.
    </p>

    <h2>Idempotency</h2>
    <p>
      For state-changing requests pass an <code>Idempotency-Key</code> header (any UUID). We dedupe
      replays within 24h. Doubly important for <code>tools/call</code>.
    </p>

    <h2>Selected endpoints</h2>
    <CodeBlock>{`# Identity
POST   /auth/sign-up
POST   /auth/sign-in
POST   /auth/sign-out
GET    /me

# Projects
GET    /projects
POST   /projects
GET    /projects/{slug}
PATCH  /projects/{slug}
DELETE /projects/{slug}
GET    /projects/{slug}/audit
POST   /projects/{slug}/mcp/rotate

# Tools
GET    /projects/{slug}/tools
POST   /projects/{slug}/tools
GET    /tools/{id}
PATCH  /tools/{id}
POST   /tools/{id}/call
POST   /tools/{id}/disable
POST   /tools/{id}/enable
GET    /projects/{slug}/tools/export?format=anthropic

# Credentials
GET    /credentials
POST   /credentials
POST   /credentials/{id}/rotate
DELETE /credentials/{id}

# Memory + vectors
GET    /projects/{slug}/memory/stores
POST   /projects/{slug}/memory/stores
POST   /memory/stores/{id}/upsert
POST   /memory/stores/{id}/recall
POST   /vectors/{index}/search

# MCP
GET    /projects/{slug}/mcp/servers
POST   /projects/{slug}/mcp/servers
DELETE /projects/{slug}/mcp/servers/{name}

# Webhooks
GET    /webhooks
POST   /webhooks
POST   /webhooks/{id}/ping`}</CodeBlock>

    <h2>Webhooks signature</h2>
    <p>
      We sign every webhook delivery with HMAC-SHA256 over the body using the secret you registered.
      Verify it before trusting the payload:
    </p>
    <CodeBlock lang="ts">{`const sig = req.headers["x-memkernel-signature"];
const expected = crypto
  .createHmac("sha256", process.env.MK_WEBHOOK_SECRET!)
  .update(req.rawBody)
  .digest("hex");
if (!crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected))) {
  return res.status(400).end();
}`}</CodeBlock>
  </>
);

const GraphqlApiBody = (
  <>
    <p>
      A GraphQL surface over the same domain is on the roadmap. Schema-first (we publish the
      .graphql), fully typed, with subscriptions for tool calls, audit events, and agent runs.
      <Soon />
    </p>

    <h2>What we're planning</h2>
    <ul>
      <li>
        Single endpoint: <code>https://api.memkernel.com/graphql</code>
      </li>
      <li>Cursor + relay-style connections for every list type.</li>
      <li>Streaming subscriptions over GraphQL-WS.</li>
      <li>Schema published as a versioned SDL + generated TS / Python clients.</li>
      <li>Same auth as REST (bearer header).</li>
    </ul>

    <h2>Sample query (preview)</h2>
    <CodeBlock lang="graphql">{`query MyProject($slug: String!) {
  project(slug: $slug) {
    name
    tools(first: 50) {
      nodes { id name description tags }
      pageInfo { endCursor hasNextPage }
    }
    auditEvents(first: 25, filter: { effect: DENY }) {
      nodes { calledAt caller { id } target reason }
    }
  }
}`}</CodeBlock>

    <p>
      Most users will keep using REST + SDKs. GraphQL is shipping primarily for dashboard-style
      integrations where a single multi-resource query saves round-trips.
    </p>
  </>
);

const McpApiBody = (
  <>
    <p>
      Every project exposes a streamable-HTTP MCP endpoint at{" "}
      <code>https://mcp.memkernel.com/p/&lt;slug&gt;</code>. The endpoint authenticates with a
      per-project MCP token; rotate it from the dashboard or via REST.
    </p>

    <h2>Protocol version</h2>
    <p>
      MemKernel speaks MCP <strong>2025-06-18</strong> (the version Claude Desktop 1.0.6+ and Cursor
      0.43+ ship). We auto-negotiate down to <code>2025-03-26</code> for older clients.
    </p>

    <h2>Claude Desktop</h2>
    <CodeBlock lang="json">{`{
  "mcpServers": {
    "memkernel-coding": {
      "url":  "https://mcp.memkernel.com/p/coding",
      "auth": { "kind": "bearer", "token": "mks_mcp_..." }
    }
  }
}`}</CodeBlock>

    <h2>Cursor</h2>
    <CodeBlock lang="json">{`{
  "mcp.servers": {
    "memkernel-coding": {
      "url": "https://mcp.memkernel.com/p/coding",
      "headers": { "Authorization": "Bearer mks_mcp_..." }
    }
  }
}`}</CodeBlock>

    <h2>Windsurf</h2>
    <CodeBlock lang="json">{`{
  "mcp_servers": {
    "memkernel-coding": {
      "url": "https://mcp.memkernel.com/p/coding",
      "auth_token": "mks_mcp_..."
    }
  }
}`}</CodeBlock>

    <h2>Open WebUI</h2>
    <p>
      Settings → MCP → Add server. URL =<code>https://mcp.memkernel.com/p/coding</code>; Bearer ={" "}
      <code>mks_mcp_…</code>. Restart Open WebUI to register tools.
    </p>

    <h2>Tool discovery</h2>
    <p>
      On connection the client calls <code>tools/list</code> and we return every enabled tool in the
      project, filtered by the bearer's permissions. Tool names are namespaced —{" "}
      <code>gmail.search</code>, <code>github.create_issue</code>. We never expose internal IDs to
      the client.
    </p>

    <h2>Health + version negotiation</h2>
    <CodeBlock>{`GET /p/coding/_meta
# → 200 {
#   "protocolVersions": ["2025-06-18", "2025-03-26"],
#   "transport":        "http-streamable",
#   "tools":            128,
#   "lastChangedAt":    "2026-05-29T01:02:00Z"
# }`}</CodeBlock>

    <h2>Token rotation</h2>
    <CodeBlock lang="bash">{`curl -X POST https://api.memkernel.com/v1/projects/coding/mcp/rotate \\
  -H "Authorization: Bearer mks_user_..."
# → { "token": "mks_mcp_NEW..." }   # old token stops working immediately`}</CodeBlock>

    <h2>Errors the client will see</h2>
    <table>
      <thead>
        <tr>
          <th>Code</th>
          <th>Meaning</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <code>mcp_token_invalid</code>
          </td>
          <td>Bearer doesn't match the project's current MCP token</td>
        </tr>
        <tr>
          <td>
            <code>tool_not_found</code>
          </td>
          <td>The tool was deleted or disabled</td>
        </tr>
        <tr>
          <td>
            <code>permission_denied</code>
          </td>
          <td>Caller isn't allowed to call this tool</td>
        </tr>
        <tr>
          <td>
            <code>upstream_unavailable</code>
          </td>
          <td>The connector this tool wraps is failing</td>
        </tr>
        <tr>
          <td>
            <code>rate_limited</code>
          </td>
          <td>Per-minute rate-limit hit</td>
        </tr>
      </tbody>
    </table>
  </>
);

const WebhooksBody = (
  <>
    <p>
      Webhooks deliver structured events to your URL so you can react in real-time — kick off a
      follow-up workflow, page on-call, log to your own warehouse. HMAC-signed, retried, dedupable.
      <Soon />
    </p>

    <h2>Subscribe</h2>
    <CodeBlock lang="ts">{`await mk.webhooks.create({
  url:    "https://yourapp.example.com/mk-events",
  secret: process.env.MK_WEBHOOK_SECRET,    // we sign with this
  events: [
    "tool.called",
    "tool.failed",
    "credential.expiring",
    "credential.revoked",
    "agent.run.started",
    "agent.run.completed",
    "agent.run.failed",
    "deploy.applied",
    "deploy.rolled_back",
  ],
  // Optional: only fire when this filter matches the event payload
  filter: { project: "personal-assistant" },
});`}</CodeBlock>

    <h2>Event shape</h2>
    <CodeBlock lang="json">{`{
  "id":        "evt_01H...",
  "type":      "tool.called",
  "createdAt": "2026-05-29T01:02:00Z",
  "project":   "personal-assistant",
  "data": {
    "tool":     "gmail.search",
    "caller":   { "kind": "client", "id": "claude-desktop-abc" },
    "args":     { "q": "is:unread" },
    "result":   { "status": "ok", "count": 4 },
    "latencyMs": 142
  }
}`}</CodeBlock>

    <h2>Verifying signatures</h2>
    <CodeBlock lang="ts">{`import crypto from "node:crypto";

app.post("/mk-events", express.raw({ type: "*/*" }), (req, res) => {
  const sig = req.header("x-memkernel-signature") ?? "";
  const expected = crypto
    .createHmac("sha256", process.env.MK_WEBHOOK_SECRET!)
    .update(req.body)
    .digest("hex");
  if (!crypto.timingSafeEqual(Buffer.from(sig), Buffer.from(expected))) {
    return res.status(400).end();
  }
  const event = JSON.parse(req.body.toString());
  // ...
  res.status(204).end();
});`}</CodeBlock>

    <h2>Retries</h2>
    <p>
      Non-2xx response or timeout (5s default) triggers retries at
      <code>30s, 2m, 15m, 1h, 4h, 12h</code>. After all retries, the delivery is logged as failed
      and surfaced in the dashboard.
    </p>

    <h2>Ordering + dedup</h2>
    <p>
      We don't guarantee strict ordering across event types. We do guarantee at-least-once delivery;
      each event carries a stable <code>id</code> for dedup at your side.
    </p>
  </>
);

const SdkTsBody = (
  <>
    <p>
      The TypeScript SDK is the most full-featured of the three. Strict TS types for every endpoint,
      streaming for long-running calls, first-class adapters for OpenAI, Anthropic, the Vercel AI
      SDK, and LangChain.
    </p>

    <h2>Install</h2>
    <CodeBlock lang="bash">{`pnpm add @memkernel/sdk
# or: npm i @memkernel/sdk  ·  yarn add @memkernel/sdk
# Runtimes: Node 20+, Bun 1.2+, Deno 1.40+, modern browsers (proxy)`}</CodeBlock>

    <h2>Configure</h2>
    <CodeBlock lang="ts">{`import { memkernel } from "@memkernel/sdk";

const mk = memkernel({
  apiKey:   process.env.MK_API_KEY!,
  baseUrl:  process.env.MK_BASE_URL,     // self-host
  timeout:  30_000,
  retries:  { max: 3, backoff: "exponential" },
  callerId: "agent:weekly-briefing",     // shows up in audit + permission matching
});`}</CodeBlock>

    <h2>Common patterns</h2>

    <h3>Export tools for any model</h3>
    <CodeBlock lang="ts">{`const tools = await mk.tools.export({
  project: "personal-assistant",
  format:  "anthropic",   // or "openai" | "mcp" | "langchain" | "mastra"
});`}</CodeBlock>

    <h3>Call a tool directly</h3>
    <CodeBlock lang="ts">{`const res = await mk.tools.call({
  tool: "gmail.search",
  args: { q: "from:billing@stripe.com newer_than:7d" },
});`}</CodeBlock>

    <h3>Run an agent loop (streamed)</h3>
    <CodeBlock lang="ts">{`const stream = await mk.agents.runStream({
  project: "personal-assistant",
  model:   "anthropic:claude-sonnet-4-6",
  messages: [{ role: "user", content: "Summarise my unread emails." }],
});

for await (const ev of stream) {
  if (ev.type === "text")        process.stdout.write(ev.delta);
  if (ev.type === "tool_use")    console.log("→ calling", ev.name);
  if (ev.type === "tool_result") console.log("←", ev.preview);
}`}</CodeBlock>

    <h3>Custom handler hosted in MemKernel</h3>
    <CodeBlock lang="ts">{`await mk.tools.create({
  project: "coding",
  name:    "find_issue_with_label",
  description: "Find the latest GitHub issue with a given label.",
  inputSchema: z.object({ label: z.string() }),
  handler: async ({ label }, ctx) => {
    const { issues } = await ctx.providers.github.issues.list({
      labels: [label], state: "open", per_page: 1,
    });
    return issues[0] ?? null;
  },
});`}</CodeBlock>

    <h2>Adapters</h2>
    <ul>
      <li>
        <strong>OpenAI</strong> — <code>tools = mk.tools.openai("project")</code> returns the exact
        shape Chat Completions wants.
      </li>
      <li>
        <strong>Anthropic</strong> — <code>tools = mk.tools.anthropic("project")</code>.
      </li>
      <li>
        <strong>Vercel AI SDK</strong> — drop-in via{" "}
        <code>{`import { mkTools } from "@memkernel/sdk/vercel-ai"`}</code>.
      </li>
      <li>
        <strong>LangChain TS</strong> — <code>{`new MemKernelLoadout(mk, "project")`}</code> plugs
        into an Agent Executor.
      </li>
    </ul>

    <h2>Errors</h2>
    <p>
      Every error is an instance of <code>MemKernelError</code> with a typed <code>code</code>{" "}
      field, the original HTTP status, and the request id for audit lookup.
    </p>
    <CodeBlock lang="ts">{`try {
  await mk.tools.call({ tool: "gmail.send", args: { ... } });
} catch (err) {
  if (err instanceof MemKernelError && err.code === "permission_denied") {
    // ...
  }
  throw err;
}`}</CodeBlock>

    <h2>Testing</h2>
    <p>Inject a mock transport for unit tests — no network calls required:</p>
    <CodeBlock lang="ts">{`import { memkernel, mockTransport } from "@memkernel/sdk/testing";

const mk = memkernel({ transport: mockTransport({
  "POST /v1/tools/gmail.search/call": () => ({ data: [{ id: "msg_1" }] }),
}) });`}</CodeBlock>
  </>
);

const SdkPythonBody = (
  <>
    <p>
      The Python SDK matches the TS surface 1:1. Async-first (sync wrappers provided), supports
      Python 3.10+, ships with Pydantic types and adapters for OpenAI, Anthropic, and LangChain.
    </p>

    <h2>Install</h2>
    <CodeBlock lang="bash">{`pip install memkernel
# or: poetry add memkernel  ·  uv add memkernel`}</CodeBlock>

    <h2>Configure</h2>
    <CodeBlock lang="python">{`from memkernel import MemKernel

mk = MemKernel(
    api_key=os.environ["MK_API_KEY"],
    base_url=os.environ.get("MK_BASE_URL"),
    timeout=30,
    retries=3,
    caller_id="agent:weekly-briefing",
)`}</CodeBlock>

    <h2>Export tools for any model</h2>
    <CodeBlock lang="python">{`tools = mk.tools.export(
    project="personal-assistant",
    format="anthropic",   # or "openai" | "mcp" | "langchain" | "mastra"
)

import anthropic
client = anthropic.Anthropic()
reply = client.messages.create(
    model="claude-sonnet-4-6",
    tools=tools,
    messages=[{"role": "user", "content": "Summarise unread email."}],
)`}</CodeBlock>

    <h2>Async + streaming</h2>
    <CodeBlock lang="python">{`from memkernel import AsyncMemKernel

mk = AsyncMemKernel(api_key=os.environ["MK_API_KEY"])

async def main():
    async for ev in mk.agents.run_stream(
        project="personal-assistant",
        model="anthropic:claude-sonnet-4-6",
        messages=[{"role": "user", "content": "..."}],
    ):
        if ev.type == "text":
            print(ev.delta, end="", flush=True)
        elif ev.type == "tool_use":
            print(f"→ {ev.name}")`}</CodeBlock>

    <h2>Custom handler in Python</h2>
    <CodeBlock lang="python">{`from pydantic import BaseModel

class Inputs(BaseModel):
    label: str

@mk.tool(project="coding", inputs=Inputs)
async def find_issue_with_label(args: Inputs, ctx):
    issues = await ctx.providers.github.issues.list(
        labels=[args.label], state="open", per_page=1,
    )
    return issues[0] if issues else None`}</CodeBlock>

    <h2>LangChain</h2>
    <CodeBlock lang="python">{`from langchain_openai import ChatOpenAI
from memkernel.langchain import load_tools

tools = load_tools(project="personal-assistant", api_key=os.environ["MK_API_KEY"])
agent = ChatOpenAI(model="gpt-4o").bind_tools(tools)`}</CodeBlock>

    <h2>Testing</h2>
    <CodeBlock lang="python">{`from memkernel.testing import MockTransport

mk = MemKernel(transport=MockTransport({
    "POST /v1/tools/gmail.search/call": lambda req: {"data": [{"id": "msg_1"}]},
}))`}</CodeBlock>
  </>
);

const SdkGoBody = (
  <>
    <p>
      The Go SDK targets Go 1.22+, generics-aware typed handlers, context-based cancellation, and
      structured errors. In active development.
      <Soon />
    </p>

    <h2>Install</h2>
    <CodeBlock lang="bash">{`go get github.com/memkernel/sdk-go`}</CodeBlock>

    <h2>Configure</h2>
    <CodeBlock lang="go">{`package main

import (
    "context"
    "os"

    "github.com/memkernel/sdk-go"
)

func main() {
    mk := memkernel.New(
        memkernel.WithAPIKey(os.Getenv("MK_API_KEY")),
        memkernel.WithBaseURL(os.Getenv("MK_BASE_URL")),
        memkernel.WithRetries(3),
        memkernel.WithCallerID("agent:weekly-briefing"),
    )

    ctx := context.Background()
    p, err := mk.Projects.Get(ctx, "coding")
    if err != nil { panic(err) }
    _ = p
}`}</CodeBlock>

    <h2>Export tools</h2>
    <CodeBlock lang="go">{`tools, err := mk.Tools.Export(ctx, memkernel.ExportOptions{
    Project: "personal-assistant",
    Format:  memkernel.FormatAnthropic,
})`}</CodeBlock>

    <h2>Custom handler</h2>
    <CodeBlock lang="go">{`type FindIssue struct {
    Label string \`json:"label"\`
}

err := mk.Tools.Create(ctx, memkernel.Tool{
    Project:     "coding",
    Name:        "find_issue_with_label",
    Description: "Find the latest GitHub issue with a given label.",
    Inputs:      memkernel.SchemaFor[FindIssue](),
    Handler: memkernel.HandlerFunc[FindIssue](func(
        ctx context.Context, in FindIssue, c *memkernel.Context,
    ) (any, error) {
        issues, _, err := c.Providers.GitHub.Issues.List(ctx, &github.IssueListOptions{
            Labels: []string{in.Label}, State: "open", PerPage: 1,
        })
        if err != nil { return nil, err }
        if len(issues) == 0 { return nil, nil }
        return issues[0], nil
    }),
})`}</CodeBlock>

    <h2>Streaming</h2>
    <CodeBlock lang="go">{`stream, err := mk.Agents.RunStream(ctx, memkernel.RunOptions{
    Project:  "personal-assistant",
    Model:    "anthropic:claude-sonnet-4-6",
    Messages: []memkernel.Message{{Role: "user", Content: "Summarise email."}},
})
if err != nil { panic(err) }

for ev := range stream.Events() {
    switch e := ev.(type) {
    case memkernel.TextEvent:     fmt.Print(e.Delta)
    case memkernel.ToolUseEvent:  fmt.Println("→", e.Name)
    }
}`}</CodeBlock>

    <h2>Errors</h2>
    <CodeBlock lang="go">{`if err != nil {
    var apiErr *memkernel.APIError
    if errors.As(err, &apiErr) && apiErr.Code == "permission_denied" {
        // ...
    }
    return err
}`}</CodeBlock>
  </>
);

const SelfHostingBody = (
  <>
    <p>
      MemKernel is open source and built for self-hosting from day one. The default stack is a
      single Docker image + Postgres. Optional pieces (Redis for shared rate-limiting, dedicated
      vector DB, external KMS) plug in as you scale.
    </p>

    <h2>Hardware sizing</h2>
    <table>
      <thead>
        <tr>
          <th>Scale</th>
          <th>App</th>
          <th>Postgres</th>
          <th>Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1–10 users / hobby</td>
          <td>1 × 1 vCPU / 1 GB</td>
          <td>1 GB</td>
          <td>SQLite vector index</td>
        </tr>
        <tr>
          <td>10–100 users</td>
          <td>2 × 2 vCPU / 2 GB</td>
          <td>4 GB / 50 GB SSD</td>
          <td>pgvector OK</td>
        </tr>
        <tr>
          <td>100+ users / production</td>
          <td>3+ × 4 vCPU / 4 GB</td>
          <td>16 GB / 200 GB SSD + replica</td>
          <td>Add Redis + Qdrant</td>
        </tr>
      </tbody>
    </table>

    <h2>Required environment</h2>
    <CodeBlock lang="bash">{`# Postgres connection string. We DO NOT create the database;
# point this at a Postgres 16+ with the role you intend to use.
DATABASE_URL=postgres://memkernel:secret@db:5432/memkernel

# Random 32-byte hex. Used to sign session tokens.
SESSION_SECRET=<openssl rand -hex 32>

# Random 32-byte hex. Master encryption key — wraps per-user envelope
# keys for OAuth refresh tokens + API key secrets. ROTATING THIS
# without a key-derivation transition will break every credential.
ENCRYPTION_KEY=<openssl rand -hex 32>

# Public URL the app is reachable at (used in OAuth callbacks).
PUBLIC_URL=https://memkernel.your-domain.com

# Optional but recommended:
REDIS_URL=redis://redis:6379
S3_BUCKET=memkernel-uploads
S3_REGION=eu-central-1
S3_ENDPOINT=https://s3.eu-central-1.amazonaws.com
KMS_PROVIDER=aws | gcp | vault | none
SMTP_URL=smtps://user:pass@smtp.acme.com`}</CodeBlock>

    <h2>Run it</h2>
    <CodeBlock lang="bash">{`docker run -d \\
  -e DATABASE_URL=postgres://… \\
  -e SESSION_SECRET=$(openssl rand -hex 32) \\
  -e ENCRYPTION_KEY=$(openssl rand -hex 32) \\
  -e PUBLIC_URL=https://memkernel.your-domain.com \\
  -p 8080:8080 \\
  ghcr.io/memkernel/memkernel:latest`}</CodeBlock>

    <h2>Reverse proxy + TLS</h2>
    <p>
      MemKernel speaks plain HTTP on 8080. Terminate TLS at your proxy of choice. Long-lived MCP
      streams need <code>proxy_buffering off</code> on nginx + <code>proxy_read_timeout 1h</code>;
      equivalents on Caddy / Traefik are documented in the example configs in the repo.
    </p>

    <h2>Migrations</h2>
    <p>
      We apply DB migrations on boot inside an advisory lock — safe to run multiple instances. To
      run them out of band:
    </p>
    <CodeBlock lang="bash">{`docker run --rm \\
  -e DATABASE_URL=postgres://... \\
  ghcr.io/memkernel/memkernel:latest \\
  /app/bin/migrate up`}</CodeBlock>

    <h2>Upgrades</h2>
    <p>
      Pin to a tag (<code>:1.2.0</code>), not <code>:latest</code>. Read the{" "}
      <Link to="/docs/changelog">changelog</Link> before bumping across a major. We never break{" "}
      <code>v1</code> APIs within a major; storage migrations may need a maintenance window above
      ~10M tool calls.
    </p>

    <h2>Health + metrics</h2>
    <ul>
      <li>
        <code>GET /health/live</code> — liveness, returns 200 always when alive.
      </li>
      <li>
        <code>GET /health/ready</code> — readiness, returns 503 until Postgres reachable.
      </li>
      <li>
        <code>GET /metrics</code> — Prometheus format.
      </li>
      <li>
        <code>GET /admin/queues</code> — current queue depths (Redis only).
      </li>
    </ul>

    <h2>Bring your own KMS</h2>
    <p>
      Optional: instead of holding <code>ENCRYPTION_KEY</code> in env, wrap it with AWS KMS / GCP
      KMS / HashiCorp Vault. The
      <code>KMS_PROVIDER</code> env switches behaviour; per-provider config is in the chart values.
    </p>
  </>
);

const DockerBody = (
  <>
    <p>
      Our production image is the same one we run on Railway. Multi-stage build (pnpm install → Bun
      runtime), small surface, no shell by default. We tag stable releases at <code>:1.X</code>,
      edge at <code>:edge</code>.
    </p>

    <h2>Single-host docker-compose</h2>
    <CodeBlock lang="yaml">{`version: "3.9"

services:
  postgres:
    image: postgres:16
    environment:
      POSTGRES_USER:     memkernel
      POSTGRES_PASSWORD: \${POSTGRES_PASSWORD}
      POSTGRES_DB:       memkernel
    volumes:
      - pgdata:/var/lib/postgresql/data
    healthcheck:
      test: ["CMD-SHELL", "pg_isready -U memkernel"]
      interval: 5s
      timeout: 3s
      retries: 5

  redis:
    image: redis:7
    command: ["redis-server", "--save", "", "--appendonly", "no"]

  memkernel:
    image: ghcr.io/memkernel/memkernel:1
    restart: always
    depends_on:
      postgres: { condition: service_healthy }
      redis:    { condition: service_started }
    environment:
      DATABASE_URL:    postgres://memkernel:\${POSTGRES_PASSWORD}@postgres:5432/memkernel
      REDIS_URL:       redis://redis:6379
      SESSION_SECRET:  \${SESSION_SECRET}
      ENCRYPTION_KEY:  \${ENCRYPTION_KEY}
      PUBLIC_URL:      \${PUBLIC_URL}
    ports: ["8080:8080"]

volumes:
  pgdata: {}`}</CodeBlock>

    <h2>Bootstrap</h2>
    <CodeBlock lang="bash">{`# Generate secrets once
echo POSTGRES_PASSWORD=$(openssl rand -hex 16) >> .env
echo SESSION_SECRET=$(openssl rand -hex 32)    >> .env
echo ENCRYPTION_KEY=$(openssl rand -hex 32)    >> .env
echo PUBLIC_URL=https://memkernel.example.com  >> .env

docker compose up -d
# DB migrations run on boot; first start takes ~20s.

# Tail logs until "memkernel listening on :8080"
docker compose logs -f memkernel`}</CodeBlock>

    <h2>Persistent data</h2>
    <ul>
      <li>
        <strong>Postgres volume</strong> — everything that matters lives here. Back up nightly with{" "}
        <code>pg_dump</code> + ship to S3.
      </li>
      <li>
        <strong>S3 bucket</strong> — set <code>S3_BUCKET</code> if you accept file uploads
        (knowledge-base sources).
      </li>
      <li>
        <strong>Redis</strong> — ephemeral; safe to lose. Used for rate limiting + in-flight job
        queues only.
      </li>
    </ul>

    <h2>Backups + restore</h2>
    <CodeBlock lang="bash">{`# Backup
docker exec postgres pg_dump -U memkernel memkernel \\
  | gzip > memkernel-\$(date +%Y%m%d).sql.gz

# Restore (fresh DB)
gunzip -c memkernel-20260529.sql.gz \\
  | docker exec -i postgres psql -U memkernel memkernel`}</CodeBlock>

    <h2>Updates</h2>
    <CodeBlock lang="bash">{`docker compose pull
docker compose up -d
# Migrations run inside an advisory lock; rolling restarts are safe.`}</CodeBlock>
  </>
);

const KubernetesBody = (
  <>
    <p>
      A first-party Helm chart and example manifests are in active development.
      <Soon /> Track progress on{" "}
      <a href="https://github.com/cemsina/memkernel" target="_blank" rel="noreferrer">
        GitHub
      </a>
      .
    </p>

    <h2>Shape of the chart</h2>
    <ul>
      <li>
        <code>Deployment</code> with HPA on CPU + custom metric (<code>mk_inflight_tool_calls</code>
        ).
      </li>
      <li>
        <code>Service</code> + ingress (nginx-ingress + cert-manager defaults).
      </li>
      <li>
        Optional sub-chart: <code>bitnami/postgresql</code>, <code>bitnami/redis</code>,{" "}
        <code>qdrant/qdrant</code>.
      </li>
      <li>
        <code>NetworkPolicy</code> sample — egress restricted to the Postgres + your OAuth
        providers' IPs only.
      </li>
      <li>
        <code>ExternalSecret</code> integration for AWS Secrets Manager / Vault / GCP Secret
        Manager.
      </li>
      <li>
        Pre-built <code>ServiceMonitor</code> for Prometheus + <code>PrometheusRule</code> alerts.
      </li>
    </ul>

    <h2>Until then</h2>
    <p>
      The Docker image runs as-is in any K8s — apply a Deployment + Service + a managed Postgres
      (RDS, CloudSQL, Neon). The hand-rolled manifests in <code>/deployments/k8s/</code> on the
      GitHub repo are a fine starting point.
    </p>
  </>
);

const ChangelogBody = (
  <>
    <p>
      We follow{" "}
      <a href="https://semver.org" target="_blank" rel="noreferrer">
        semver
      </a>{" "}
      for the API surface. Within a major version, we never break a published response shape.
      Storage migrations are called out explicitly so self-hosters can plan windows.
    </p>

    <h2>2026-05-29 · 0.2.0 (current)</h2>
    <ul>
      <li>
        <strong>Web rebuild.</strong> Marketing + docs site, 319-connector catalog, animated
        FlowShowcase + agent-deploy showcase, IDE-style code blocks (Shiki), syntax-highlighted SDK
        switcher.
      </li>
      <li>
        <strong>Docs overhaul.</strong> Every page got a context-appropriate animated visual,
        tightened prose, real code examples, error codes, and FAQ-style edge cases.
      </li>
    </ul>

    <h2>2026-05-23 · 0.1.0 · Phase 0 foundation</h2>
    <ul>
      <li>
        <strong>Pivot</strong> from the typed-knowledge prototype to a control plane for AI agents.
        Old code preserved on the <code>legacy</code> branch.
      </li>
      <li>
        <strong>Stack:</strong> TypeScript end-to-end (Hono + Bun + Drizzle + Postgres). React +
        Vite frontend.
      </li>
      <li>
        Monorepo scaffold: <code>apps/server</code>, <code>apps/web</code>, <code>packages/db</code>
        .
      </li>
      <li>Initial schema: users, sessions, projects, credentials.</li>
    </ul>

    <h2>In progress · Phase 1</h2>
    <ul>
      <li>Email/password + Google + GitHub auth.</li>
      <li>Tool registry + per-project MCP endpoint.</li>
      <li>Google Drive as the first first-party connector.</li>
      <li>SDK preview release (TypeScript, then Python, then Go).</li>
    </ul>

    <h2>Roadmap · Phase 2</h2>
    <ul>
      <li>Memory + vector layer GA.</li>
      <li>Knowledge base ingestion (Drive, Notion, web).</li>
      <li>10+ OAuth connectors landed monthly.</li>
      <li>Multi-protocol tool export (OpenAI / Anthropic / LangChain).</li>
    </ul>

    <h2>Roadmap · Phase 3</h2>
    <ul>
      <li>Agent runtime + skills.sh import.</li>
      <li>Scheduling + deployments.</li>
      <li>Marketplace.</li>
    </ul>
  </>
);

const AboutBody = (
  <>
    <p>
      MemKernel is a small team's bet that the right shape for AI infrastructure isn't another chat
      product or another framework. It's the layer underneath every agent — tools, memory, identity,
      permissions, and eventually agent execution — sitting in one control plane that both Claude
      and your own code can talk to.
    </p>

    <h2>The thesis</h2>
    <p>
      Today every agent codebase ends up reimplementing the same dozen integrations and
      re-authorizing the same OAuth providers because the integration layer is bundled inside
      whichever framework the author chose. Swap frameworks (or worse, ship to two of them) and the
      whole integration tier gets rebuilt.
    </p>
    <p>
      MemKernel inverts that:{" "}
      <strong>
        integrations live in one place; agents reach for them in whatever shape they need.
      </strong>{" "}
      We cover memory, vectors, identity, permissions, and (next) agent runtime under one roof.
    </p>

    <h2>Values</h2>
    <ul>
      <li>
        <strong>Boring infra, exciting agents.</strong> Postgres, TypeScript, Docker. Single binary
        self-host. No exotic ops.
      </li>
      <li>
        <strong>Open source.</strong> Apache-2.0 where we need to be, permissive elsewhere.
        Self-hosters get the same code we run.
      </li>
      <li>
        <strong>Personal-account only.</strong> No leaky workspaces. Most upstream OAuth providers
        are personal-scoped; pretending otherwise creates security holes.
      </li>
      <li>
        <strong>Minimize dependencies.</strong> If Postgres can do it, we don't add Redis. If
        pgvector can do it, we don't add Qdrant.
      </li>
    </ul>

    <h2>Contact</h2>
    <p>
      <a href="mailto:hello@memkernel.com">hello@memkernel.com</a> · {""}
      <a href="https://github.com/cemsina/memkernel" target="_blank" rel="noreferrer">
        GitHub
      </a>{" "}
      ·{" "}
      <a href="https://twitter.com/memkernel" target="_blank" rel="noreferrer">
        Twitter
      </a>
    </p>
  </>
);

const PrivacyBody = (
  <>
    <p>
      Two-line summary: we collect the minimum data needed to operate. Your tool calls, memory
      writes, OAuth tokens, and audit logs are stored encrypted at rest, never sold, never used to
      train models. This page is a developer-friendly summary; the legally-binding version lives at{" "}
      <Link to="/docs/terms">/docs/terms</Link>.
    </p>

    <h2>What we store</h2>
    <ul>
      <li>
        <strong>Account identity.</strong> Email + Argon2id-hashed password, OAuth identities
        (Google / GitHub), display name.
      </li>
      <li>
        <strong>Credentials.</strong> OAuth refresh tokens + API keys you've added — AES-256-GCM
        encrypted under a per-user envelope key wrapped by our KMS (or your own if self-hosting).
      </li>
      <li>
        <strong>Project state.</strong> Project + tool definitions, memory store contents, vector
        index contents, knowledge-base chunks.
      </li>
      <li>
        <strong>Audit logs.</strong> Every tool call: caller, args (redacted by schema), result
        code, latency. 90 days default retention; configurable.
      </li>
      <li>
        <strong>Request metadata.</strong> IPs (truncated to /24 or /48), user agents, HTTP method +
        path. 30 days for ops debugging.
      </li>
    </ul>

    <h2>What we DON'T do</h2>
    <ul>
      <li>Read your decrypted credentials. Only the per-tool runtime ever sees the plaintext.</li>
      <li>Train models on your data. We don't run a foundation model.</li>
      <li>Sell or share user data with third parties for marketing.</li>
      <li>
        Log credential <em>values</em> — only credential metadata (which provider, when added).
      </li>
    </ul>

    <h2>Subprocessors</h2>
    <ul>
      <li>
        <strong>Hosting:</strong> Railway (data-plane), Cloudflare (edge + CDN).
      </li>
      <li>
        <strong>Database:</strong> Neon (managed Postgres) in EU + US regions.
      </li>
      <li>
        <strong>Email:</strong> Resend (transactional only).
      </li>
      <li>
        <strong>Error tracking:</strong> Sentry (stack traces only; we scrub PII).
      </li>
    </ul>
    <p>
      Full list with versions is at{" "}
      <a href="https://memkernel.com/subprocessors" target="_blank" rel="noreferrer">
        memkernel.com/subprocessors
      </a>{" "}
      and updated 30 days before any addition.
    </p>

    <h2>Data subject rights</h2>
    <ul>
      <li>
        <strong>Export.</strong> Settings → Account → Export. We email a ZIP within 24h.
      </li>
      <li>
        <strong>Delete.</strong> Settings → Account → Delete. Soft-deleted for 30 days (in case you
        change your mind), then hard-purged.
      </li>
      <li>
        <strong>Rectification.</strong> Most fields editable from the dashboard. Email us for
        anything else.
      </li>
    </ul>

    <h2>Reporting a security issue</h2>
    <p>
      Email <a href="mailto:security@memkernel.com">security@memkernel.com</a>. We acknowledge
      within 24h and run a public, time-bounded coordinated disclosure. Bounty program in{" "}
      <Link to="/docs/changelog">roadmap</Link>.
    </p>
  </>
);

const TermsBody = (
  <>
    <p>
      The hosted MemKernel service is governed by these terms. The open-source code is governed by
      its license (Apache 2.0). Reading one doesn't waive your obligations under the other.
    </p>

    <h2>Acceptable use</h2>
    <ul>
      <li>
        No automated bulk scraping of providers — we propagate provider ToS violations back to you.
        (Translation: don't use MemKernel to scrape LinkedIn at 1k req/s.)
      </li>
      <li>No traffic that violates US, EU, or your local export controls.</li>
      <li>
        No agents whose purpose is to deceive or impersonate identifiable humans without their
        consent.
      </li>
      <li>
        No reverse engineering the encryption envelope or attempting to access other tenants' data.
      </li>
    </ul>

    <h2>Service levels</h2>
    <p>
      Free plan is best-effort. Pro plan SLA: 99.5% uptime against the <code>/v1/*</code> surface,
      measured monthly. Service credits per <Link to="/docs/changelog">SLA addendum</Link>.
    </p>

    <h2>Pricing changes</h2>
    <p>
      Pricing on existing subscriptions is locked for 12 months from sign-up. New tiers and limits
      announced 30+ days in advance.
    </p>

    <h2>Data ownership</h2>
    <p>
      Your project content (tools, memory, audit logs, credentials) belongs to you. We act as data
      processor, not controller. On deletion we purge within 30 days; backups age out within 90.
    </p>

    <h2>Liability cap</h2>
    <p>
      Total liability under these terms is capped at the greater of $50,000 USD or fees paid in the
      trailing 12 months. Standard SaaS carve-outs for indemnity + IP claims.
    </p>

    <h2>Open-source license</h2>
    <p>
      The source code is Apache 2.0. Self-hosting is free. Distributing the image under a different
      name is allowed but please keep attribution intact.
    </p>

    <h2>Contact</h2>
    <p>
      Enterprise terms with DPA + SCC + custom retention:{" "}
      <a href="mailto:legal@memkernel.com">legal@memkernel.com</a>.
    </p>
  </>
);

// ── Registry ─────────────────────────────────────────────────────────

export const DOC_CONTENT: Record<string, DocPageEntry> = {
  "": {
    section: "Get started",
    title: "MemKernel docs",
    description: "The control plane for AI agents — connect tools, MCP, memory, and identity once.",
    visual: <ArchitectureVisual />,
    body: IntroBody,
  },
  "getting-started": {
    section: "Get started",
    title: "Quickstart",
    description: "Connect Google Drive and expose it to Claude Desktop in five minutes.",
    body: QuickstartBody,
  },
  installation: {
    section: "Get started",
    title: "Installation",
    description: "Hosted account, SDKs, and CLI install paths.",
    visual: (
      <CodeVisual
        label="Install"
        filename="terminal"
        lang="bash"
        code={`# TypeScript / Node
pnpm add @memkernel/sdk

# Python
pip install memkernel

# Go
go get github.com/memkernel/sdk-go`}
      />
    ),
    body: InstallationBody,
  },
  "first-project": {
    section: "Get started",
    title: "Your first project",
    description: "Projects are the unit of scope in MemKernel.",
    visual: <ProjectHierarchyVisual />,
    body: FirstProjectBody,
  },
  "concepts/projects": {
    section: "Core concepts",
    title: "Projects",
    description: "The unit of scope for tools, MCP, memory, and permissions.",
    visual: <ProjectHierarchyVisual />,
    body: ProjectsConceptBody,
  },
  "concepts/tools": {
    section: "Core concepts",
    title: "Tools",
    description: "A function an agent can call, exportable to every shape.",
    visual: <ToolExportVisual />,
    body: ToolsConceptBody,
  },
  "concepts/mcp": {
    section: "Core concepts",
    title: "MCP servers",
    description: "How MemKernel uses the Model Context Protocol as both input and output.",
    visual: <McpUrlVisual />,
    body: McpConceptBody,
  },
  "concepts/memory": {
    section: "Core concepts",
    title: "Memory",
    description: "User, project, agent, and shared memory.",
    visual: <MemoryRecallVisual />,
    body: MemoryConceptBody,
  },
  "concepts/vector": {
    section: "Core concepts",
    title: "Vector databases",
    description: "The unified vector + RAG layer.",
    visual: <VectorRecallVisual />,
    body: VectorConceptBody,
  },
  "concepts/permissions": {
    section: "Core concepts",
    title: "Permissions",
    description: "Decide what each client can do inside each project.",
    visual: <PermissionMatrixVisual />,
    body: PermissionsConceptBody,
  },
  connectors: {
    section: "Connectors",
    title: "Catalog overview",
    description: "310+ first-party connectors — and growing.",
    visual: <ArchitectureVisual />,
    body: ConnectorsCatalogBody,
  },
  "connectors/oauth": {
    section: "Connectors",
    title: "OAuth providers",
    description: "Connect Google, GitHub, Slack, Notion, Microsoft, and 100+ more.",
    visual: <OAuthFlowVisual />,
    body: ConnectorsOauthBody,
  },
  "connectors/api-keys": {
    section: "Connectors",
    title: "API key providers",
    description: "Use raw API keys for providers without OAuth.",
    visual: (
      <CodeVisual
        label="Add a key"
        filename="add-key.ts"
        lang="ts"
        code={`await mk.credentials.create({
  provider: "openai",
  kind: "api_key",
  label: "Personal OpenAI",
  secrets: { apiKey: process.env.OPENAI_API_KEY! },
});`}
      />
    ),
    body: ConnectorsApiKeysBody,
  },
  "connectors/custom": {
    section: "Connectors",
    title: "Custom connectors",
    description: "Wrap any HTTP API, gRPC service, or local script.",
    visual: (
      <CodeVisual
        label="Custom HTTP connector"
        filename="connectors.ts"
        lang="ts"
        code={`await mk.connectors.create({
  kind: "http",
  name: "internal-billing-api",
  baseUrl: "https://billing.internal.acme.com",
  auth: { kind: "bearer", token: process.env.BILLING_TOKEN! },
  tools: [
    {
      name: "find_subscription",
      method: "GET",
      path: "/subscriptions/find",
      query: { email: { type: "string" } },
    },
  ],
});`}
      />
    ),
    body: ConnectorsCustomBody,
  },
  tools: {
    section: "Tools & registry",
    title: "Tool registry",
    description: "The Postgres-backed source of truth for every tool.",
    visual: <ToolExportVisual />,
    body: ToolsRegistryBody,
  },
  "tools/export": {
    section: "Tools & registry",
    title: "Multi-protocol export",
    description: "MCP, OpenAI, Anthropic, LangChain, Mastra, REST — automatically.",
    visual: <ToolExportVisual />,
    body: ToolsExportBody,
  },
  "tools/permissions": {
    section: "Tools & registry",
    title: "Permissions on tools",
    description: "Grant clients specific actions with scoped arguments.",
    visual: <PermissionMatrixVisual />,
    body: ToolsPermissionsBody,
  },
  "tools/versioning": {
    section: "Tools & registry",
    title: "Versioning",
    description: "Every tool revision is preserved.",
    visual: (
      <CodeVisual
        label="Bump a tool"
        filename="versioning.ts"
        lang="ts"
        code={`await mk.tools.update("gmail.search", {
  inputSchema: nextSchema,
  changelog: "added 'in:label' filter parameter",
});`}
      />
    ),
    body: ToolsVersioningBody,
  },
  memory: {
    section: "Memory & knowledge",
    title: "Memory stores",
    description: "Typed K/V or document collections backed by Postgres.",
    visual: <MemoryRecallVisual />,
    body: MemoryStoresBody,
  },
  "memory/vector": {
    section: "Memory & knowledge",
    title: "Vector & RAG",
    description: "Embed and recall semantically inside memory.",
    visual: <VectorRecallVisual />,
    body: MemoryVectorBody,
  },
  "memory/knowledge-bases": {
    section: "Memory & knowledge",
    title: "Knowledge bases",
    description: "Higher-level wrappers around ingested documents.",
    visual: (
      <CodeVisual
        label="Create a knowledge base"
        filename="knowledge.ts"
        lang="ts"
        code={`await mk.knowledge.create({
  project: "personal-assistant",
  name: "research-docs",
  sources: [
    { kind: "drive-folder", credential: "google", folderId: "0AB..." },
    { kind: "notion-database", credential: "notion", databaseId: "..." },
  ],
});`}
      />
    ),
    body: KnowledgeBaseBody,
  },
  "memory/ingest": {
    section: "Memory & knowledge",
    title: "Ingestion sources",
    description: "Drive, Notion, S3, websites, SQL.",
    visual: (
      <CodeVisual
        label="Add a source"
        filename="ingest.ts"
        lang="ts"
        code={`await mk.knowledge.addSource("research-docs", {
  kind: "drive-folder",
  credential: "google",
  folderId: "0AB...",
  syncInterval: "1h",
  chunking: { mode: "semantic", maxTokens: 600 },
});`}
      />
    ),
    body: IngestSourcesBody,
  },
  "auth/email-password": {
    section: "Authentication",
    title: "Email + password",
    description: "The simplest path to a MemKernel account.",
    visual: (
      <CodeVisual
        label="Sign up"
        filename="terminal"
        lang="bash"
        code={`curl -X POST https://api.memkernel.com/v1/auth/sign-up \\
  -H "Content-Type: application/json" \\
  -d '{"email":"you@example.com","password":"a-very-strong-passphrase"}'`}
      />
    ),
    body: AuthEmailBody,
  },
  "auth/oauth": {
    section: "Authentication",
    title: "OAuth setup",
    description: "Sign in with Google or GitHub.",
    visual: <OAuthFlowVisual />,
    body: AuthOauthBody,
  },
  "auth/api-keys": {
    section: "Authentication",
    title: "API keys",
    description: "Project- and user-scoped keys for server-to-server access.",
    visual: (
      <CodeVisual
        label="Create a key"
        filename="terminal"
        lang="bash"
        code={`curl -X POST https://api.memkernel.com/v1/api-keys \\
  -H "Authorization: Bearer <session>" \\
  -d '{"name":"prod-coding","scope":{"project":"coding"}}'`}
      />
    ),
    body: AuthApiKeysBody,
  },
  "auth/sessions": {
    section: "Authentication",
    title: "Sessions",
    description: "Bearer tokens with a configurable TTL.",
    visual: (
      <CodeVisual
        label="Authenticated request"
        filename="terminal"
        lang="bash"
        code={`curl https://api.memkernel.com/v1/me \\
  -H "Authorization: Bearer mks_a1b2c3..."`}
      />
    ),
    body: AuthSessionsBody,
  },
  agents: {
    section: "Agents",
    title: "Agents overview",
    description: "Why we're building an agent runtime on top of the control plane.",
    visual: <ComingSoonVisual feature="Agent Runtime" />,
    body: AgentsOverviewBody,
  },
  skills: {
    section: "Agents",
    title: "Skills",
    description: "The open skill format, first-class in the agent runtime.",
    visual: (
      <CodeVisual
        label="A skill"
        filename="skills/refactor-review.md"
        lang="md"
        code={`---
name: refactor-review
description: Reviews a PR for refactor smells.
---

# Refactor review skill

When the user asks for a refactor review, …`}
      />
    ),
    body: SkillsBody,
  },
  "agents/runtime": {
    section: "Agents",
    title: "Agent runtime",
    description: "Stateful, long-running, event-driven.",
    visual: <ComingSoonVisual feature="Agent Runtime" />,
    body: RuntimeBody,
  },
  "agents/scheduling": {
    section: "Agents",
    title: "Scheduling",
    description: "Cron-style schedules for any agent.",
    visual: (
      <CodeVisual
        label="Schedule"
        filename="schedule.ts"
        lang="ts"
        code={`await mk.agents.schedule({
  agent: "weekly-report",
  cron: "0 9 * * MON",
  timezone: "Europe/Berlin",
});`}
      />
    ),
    body: SchedulingBody,
  },
  "agents/deployments": {
    section: "Agents",
    title: "Deployments",
    description: "One-click deploy to cloud / Docker / K8s.",
    visual: <ComingSoonVisual feature="Deployment Platform" />,
    body: DeploymentsBody,
  },
  "api/rest": {
    section: "APIs",
    title: "REST API",
    description: "The HTTP foundation under every SDK.",
    visual: <RestRequestVisual method="POST" path="/v1/tools/gmail.search/call" />,
    body: RestApiBody,
  },
  "api/graphql": {
    section: "APIs",
    title: "GraphQL API",
    description: "A GraphQL surface over the same domain.",
    visual: <ComingSoonVisual feature="GraphQL API" />,
    body: GraphqlApiBody,
  },
  "api/mcp": {
    section: "APIs",
    title: "MCP API",
    description: "Per-project streamable-HTTP MCP endpoints.",
    visual: <McpUrlVisual />,
    body: McpApiBody,
  },
  "api/webhooks": {
    section: "APIs",
    title: "Webhooks",
    description: "Subscribe to tool calls, credentials, agent runs.",
    visual: (
      <CodeVisual
        label="Create a webhook"
        filename="webhooks.ts"
        lang="ts"
        code={`await mk.webhooks.create({
  url: "https://yourapp.example.com/mk-events",
  events: ["tool.called", "credential.expiring", "agent.run.completed"],
});`}
      />
    ),
    body: WebhooksBody,
  },
  "sdks/typescript": {
    section: "SDKs",
    title: "TypeScript SDK",
    description: "Node 20+, Bun, Deno, and modern browsers.",
    visual: (
      <CodeVisual
        label="TypeScript SDK"
        filename="main.ts"
        lang="ts"
        code={`import { memkernel } from "@memkernel/sdk";

const mk = memkernel({ apiKey: process.env.MK_API_KEY! });

const tools = await mk.tools.export({
  project: "personal-assistant",
  format: "anthropic",
});`}
      />
    ),
    body: SdkTsBody,
  },
  "sdks/python": {
    section: "SDKs",
    title: "Python SDK",
    description: "Python 3.10+, async-first.",
    visual: (
      <CodeVisual
        label="Python SDK"
        filename="main.py"
        lang="python"
        code={`from memkernel import MemKernel

mk = MemKernel(api_key=os.environ["MK_API_KEY"])

tools = mk.tools.export(
    project="personal-assistant",
    format="anthropic",
)`}
      />
    ),
    body: SdkPythonBody,
  },
  "sdks/go": {
    section: "SDKs",
    title: "Go SDK",
    description: "Go 1.22+, idiomatic typed handlers.",
    visual: (
      <CodeVisual
        label="Go SDK"
        filename="main.go"
        lang="go"
        code={`import "github.com/memkernel/sdk-go"

mk := memkernel.New(memkernel.WithAPIKey(os.Getenv("MK_API_KEY")))

tools, err := mk.Tools.Export(ctx, memkernel.ExportOptions{
    Project: "personal-assistant",
    Format:  memkernel.FormatAnthropic,
})`}
      />
    ),
    body: SdkGoBody,
  },
  "self-hosting": {
    section: "Self-hosting",
    title: "Overview",
    description: "Single image + Postgres. Optional Redis + vector store.",
    visual: (
      <CodeVisual
        label="Run it"
        filename="terminal"
        lang="bash"
        code={`docker run -d \\
  -e DATABASE_URL=postgres://… \\
  -e SESSION_SECRET=$(openssl rand -hex 32) \\
  -e ENCRYPTION_KEY=$(openssl rand -hex 32) \\
  -p 8080:8080 \\
  ghcr.io/memkernel/memkernel:latest`}
      />
    ),
    body: SelfHostingBody,
  },
  "self-hosting/docker": {
    section: "Self-hosting",
    title: "Docker",
    description: "Multi-stage build + docker-compose example.",
    visual: (
      <CodeVisual
        label="docker-compose.yml"
        filename="docker-compose.yml"
        lang="yaml"
        code={`version: "3.9"
services:
  postgres:
    image: postgres:16
    environment:
      POSTGRES_PASSWORD: memkernel
      POSTGRES_DB: memkernel
  memkernel:
    image: ghcr.io/memkernel/memkernel:latest
    depends_on: [postgres]
    environment:
      DATABASE_URL: postgres://postgres:memkernel@postgres:5432/memkernel
      SESSION_SECRET: \${SESSION_SECRET}
      ENCRYPTION_KEY: \${ENCRYPTION_KEY}
    ports: ["8080:8080"]`}
      />
    ),
    body: DockerBody,
  },
  "self-hosting/kubernetes": {
    section: "Self-hosting",
    title: "Kubernetes",
    description: "Helm chart + example manifests (in progress).",
    visual: <ComingSoonVisual feature="Kubernetes Helm chart" />,
    body: KubernetesBody,
  },
  changelog: {
    section: "Resources",
    title: "Changelog",
    description: "What's new in MemKernel.",
    body: ChangelogBody,
  },
  about: {
    section: "Resources",
    title: "About",
    description: "Our bet, our values, our team.",
    body: AboutBody,
  },
  privacy: {
    section: "Resources",
    title: "Privacy",
    description: "What we collect, what we don't, and why.",
    body: PrivacyBody,
  },
  terms: {
    section: "Resources",
    title: "Terms",
    description: "Standard SaaS terms for hosted use; Apache 2.0 for self-host.",
    body: TermsBody,
  },
};
